import { addDays, format } from "date-fns";
import { supabase } from "./supabase";
import { fromSnake, fromSnakeList } from "./mappers";
import { effectiveHomeShiftFor } from "./tacMode";
import { hourOf, parseHourNote, bucketsBetween } from "./hourBuckets";
import type {
  Agency, Assignment, AuditEntry, Backup, CellNote, CoverageOverride,
  CoverageRequest, CoverageRequestStatus, CoverageRequestType,
  DeletionRequest, Department, Employee, EmployeeStatus, Form, IodPeriod,
  NotificationItem, OnCallAssignment, OnCallRoster, RequestField,
  RequestFieldScope, RequestFieldType, ScenarioAssignment, ScenarioTimeOff,
  ScheduleScenario, Shift, ShiftAssignment,
  ShiftBidRow, ShiftBidSheet, ShiftCode, ShiftCoverage, Subscription,
  TimeOff, TimeOffStatus, TimeOffType, User, UserLevel,
} from "./types";

// ----------------------------------------------------------------------
// Supabase-backed helpers shared by every cloud method below.

let cachedAgencyId: string | null = null;
async function cloudAgencyId(): Promise<string> {
  if (cachedAgencyId) return cachedAgencyId;
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) throw new Error("Not signed in");
  const { data } = await supabase
    .from("profiles").select("agency_id").eq("id", session.user.id).single();
  if (!data?.agency_id) throw new Error("No profile/agency");
  cachedAgencyId = data.agency_id as string;
  return cachedAgencyId;
}
export function clearAgencyCache() { cachedAgencyId = null; }

// Append an audit row via Supabase. Non-throwing: audit failure never
// blocks the business write.
async function cloudAudit(
  entityType: string,
  entityId: string,
  action: AuditEntry["action"],
  beforeJson: string | null,
  afterJson: string | null,
) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    const agencyId = await cloudAgencyId();
    await supabase.from("audit_log").insert({
      agency_id: agencyId,
      user_id: session.user.id,
      entity_type: entityType,
      entity_id: entityId,
      action,
      before_json: beforeJson ? JSON.parse(beforeJson) : null,
      after_json: afterJson ? JSON.parse(afterJson) : null,
    });
  } catch {
    /* swallow — audit is best-effort */
  }
}

// Sentence the user must type verbatim to transfer ownership.
const OWNERSHIP_SENTENCE = "I understand this action cannot be undone.";
export const OWNERSHIP_CONFIRM_SENTENCE = OWNERSHIP_SENTENCE;

// Race a thenable against a timeout; rejects with a clear error so
// login bugs surface as a user-visible message instead of a forever
// spinner. Accepts thenables (Supabase query builders are not real
// Promises) and normalises to Promise<T>.
export function withTimeout<T>(p: PromiseLike<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms`)),
      ms,
    );
    Promise.resolve(p).then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

// Build a local User shape from a Supabase auth session + profile row.
// Uses getSession() (local, cached) instead of getUser() (network) so
// boot never hangs on a cold / offline network.
async function cloudCurrentUser(): Promise<User | null> {
  const { data: { session } } = await supabase.auth.getSession();
  const au = session?.user;
  if (!au) return null;
  const { data: profile, error } = await withTimeout(
    supabase
      .from("profiles")
      .select("id, agency_id, username, email, level, employee_id, accepted_at, disabled_at, is_beta_tester")
      .eq("id", au.id)
      .maybeSingle(),
    8000,
    "profile lookup",
  );
  if (error) {
    // eslint-disable-next-line no-console
    console.error("profile lookup error", error);
    return null;
  }
  if (!profile) return null;
  if (profile.disabled_at) return null;
  return {
    id: profile.id,
    agencyId: profile.agency_id,
    username: profile.username ?? (profile.email ?? "user").split("@")[0],
    email: profile.email ?? au.email ?? "",
    passwordHash: null,
    passwordSalt: null,
    level: profile.level as UserLevel,
    role:
      profile.level === "owner" ? "admin"
      : profile.level === "l2"  ? "supervisor"
      : "employee",
    employeeId: profile.employee_id ?? null,
    acceptedAt: profile.accepted_at ?? null,
    disabledAt: profile.disabled_at ?? null,
    isBetaTester: (profile as { is_beta_tester?: boolean }).is_beta_tester === true,
  };
}

// Every write runs inside a Dexie transaction that includes auditLog so
// the change and its audit row commit atomically. The only entry points
// to the database are this file — pages never import db.ts directly.

function ymd(d: Date) { return format(d, "yyyy-MM-dd"); }

function datesInRange(start: string, end: string): string[] {
  const out: string[] = [];
  let cur = new Date(start + "T00:00:00");
  const last = new Date(end + "T00:00:00");
  while (cur <= last) {
    out.push(ymd(cur));
    cur = addDays(cur, 1);
  }
  return out;
}

// Row mapper for deletion_requests — keeps the SQL snake_case
// confined to this file. The actual table is documented in
// supabase/migrations/0043_deletion_requests.sql.
function mapDeletionRequest(r: Record<string, unknown>): DeletionRequest {
  return {
    id: r.id as string,
    userId: r.user_id as string,
    agencyId: r.agency_id as string,
    requestedAt: r.requested_at as string,
    reason: (r.reason as string | null) ?? null,
    status: r.status as DeletionRequest["status"],
    resolvedAt: (r.resolved_at as string | null) ?? null,
    resolvedBy: (r.resolved_by as string | null) ?? null,
    resolvedNote: (r.resolved_note as string | null) ?? null,
  };
}


export const api = {
  // ------------- Auth (Supabase) -------------
  // Session + identity come from Supabase Auth. Profiles carry the
  // level + agency_id the UI uses for permissions; they're created by
  // the on_auth_user_created trigger in the SQL migration.
  currentUser: cloudCurrentUser,

  login: async (args: { emailOrUsername: string; password: string }): Promise<User> => {
    const email = args.emailOrUsername.trim();
    const signInResult = await withTimeout(
      supabase.auth.signInWithPassword({ email, password: args.password }),
      10_000,
      "sign-in",
    );
    if (signInResult.error) throw new Error(signInResult.error.message);
    const userId = signInResult.data.user?.id ?? "";
    // Best-effort: try a fast profile fetch so the mutation resolves
    // with a real User on the happy path. Otherwise we return a
    // placeholder and let App.tsx's onAuthStateChange rehydrate.
    try {
      const u = await withTimeout(cloudCurrentUser(), 3_000, "profile lookup");
      if (u) {
        // Log the login after the profile fetch so cloudAgencyId
        // resolves correctly — the audit_log row needs an agency.
        await cloudAudit("user", userId, "login", null, JSON.stringify({ email }));
        return u;
      }
    } catch { /* fall through */ }
    return {
      id: userId,
      agencyId: "",
      username: email.split("@")[0],
      email,
      passwordHash: null,
      passwordSalt: null,
      level: "l4",
      role: "employee",
      employeeId: null,
      acceptedAt: null,
      disabledAt: null,
      isBetaTester: false,
    };
  },

  // Send a password-reset email via Supabase Auth. Supabase emails
  // the user a magic link that lands back on the app with
  // type=recovery in the URL hash; App.tsx's existing invite /
  // recovery deep-link handler picks up the tokens, opens a
  // session, and routes the user into AcceptInvite where they can
  // set a new password.
  //
  // Deliberately does NOT throw on unknown-email — Supabase returns
  // 200 either way to prevent account-enumeration attacks. We
  // mirror that: the UI shows the same "check your inbox" copy
  // regardless of whether the address actually exists.
  sendPasswordReset: async (email: string): Promise<void> => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      throw new Error("Enter the email address on your account.");
    }
    const { error } = await supabase.auth.resetPasswordForEmail(trimmed);
    if (error) {
      // Genuine transport errors (network down, etc.) surface;
      // "user not found" is folded into the silent-success path
      // by Supabase itself.
      throw new Error(error.message);
    }
  },

  // In-app password change for the signed-in user. Verifies the
  // current password by re-signing in with it (Supabase throws on a
  // wrong password) BEFORE issuing the update — otherwise anyone
  // with a hijacked session could rotate the password without
  // proving they know the old one. The re-signin refreshes the
  // session tokens for the same user, so it's non-disruptive.
  //
  // Both password fields are required; the new password must be at
  // least 8 characters (Supabase's own default floor, mirrored here
  // so we can surface the error before the round-trip).
  changePassword: async (args: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> => {
    const current = args.currentPassword ?? "";
    const next = args.newPassword ?? "";
    if (!current) throw new Error("Enter your current password.");
    if (next.length < 8) {
      throw new Error("New password must be at least 8 characters.");
    }
    if (next === current) {
      throw new Error("New password must be different from your current one.");
    }
    const { data: session } = await supabase.auth.getUser();
    const email = session.user?.email;
    if (!email) {
      // Should be unreachable — an authenticated call always has a
      // user with an email — but guard so we surface a clear message
      // instead of a null-deref.
      throw new Error("No signed-in account found. Sign in and try again.");
    }
    // Verify the current password. Wrong password → Supabase throws
    // "Invalid login credentials" and we relay a clearer copy.
    const verify = await supabase.auth.signInWithPassword({
      email,
      password: current,
    });
    if (verify.error) {
      throw new Error("Current password is incorrect.");
    }
    // Apply the new password on the (still-live) session.
    const { error } = await supabase.auth.updateUser({ password: next });
    if (error) throw new Error(error.message);
  },

  // Convenience wrapper: send a reset link to the signed-in user's
  // own email. Used from Settings for the "forgot the current one"
  // escape hatch on the change-password panel.
  sendPasswordResetToSelf: async (): Promise<string> => {
    const { data: session } = await supabase.auth.getUser();
    const email = session.user?.email;
    if (!email) throw new Error("No signed-in account found.");
    await api.sendPasswordReset(email);
    return email;
  },

  // Bootstrap signup: the on_auth_user_created trigger makes the first
  // user Owner of a new agency. Subsequent signups without invite
  // metadata are rejected by the trigger.
  signUp: async (args: { email: string; password: string }): Promise<User> => {
    const { error } = await supabase.auth.signUp({
      email: args.email.trim(),
      password: args.password,
    });
    if (error) throw new Error(error.message);
    const u = await cloudCurrentUser();
    if (!u) {
      throw new Error("Check your email to confirm your account, then sign in.");
    }
    return u;
  },

  logout: async (): Promise<void> => {
    // Stamp the audit row BEFORE signOut wipes the session — once
    // the session is gone, cloudAudit short-circuits because it
    // can't resolve user_id or agency_id, and the logout would
    // never make it into the log.
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await cloudAudit("user", session.user.id, "logout", null,
          JSON.stringify({ email: session.user.email }));
      }
    } catch { /* best-effort */ }
    try {
      await withTimeout(supabase.auth.signOut(), 4_000, "sign-out");
    } catch {
      // If Supabase is slow we still want to clear the cached agency
      // and drop the local session so the UI returns to the Login
      // screen rather than stalling behind a spinner.
    }
    clearAgencyCache();
  },

  // Fire the real invite email via the Supabase Edge Function. Uses
  // fetch directly rather than supabase.functions.invoke so we can
  // read the function's JSON error body on non-2xx responses instead
  // of the SDK's generic "non-2xx status code" message.
  //
  // When `level` is omitted, the Edge Function picks up the level set
  // on the employee row itself — so the invitee's account inherits
  // whatever rank the owner assigned to that employee, not a default.
  sendInvite: async (employeeId: string, level?: UserLevel):
    Promise<{ email: string }> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Not signed in");
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const res = await fetch(`${supabaseUrl}/functions/v1/send-invite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: anonKey ?? "",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ employeeId, ...(level ? { level } : {}) }),
    });
    const text = await res.text();
    let parsed: { ok?: boolean; email?: string; error?: string } = {};
    try { parsed = JSON.parse(text); } catch { /* non-JSON */ }
    if (!res.ok) {
      throw new Error(
        parsed.error
          ?? `Invite failed (HTTP ${res.status}). ${text.slice(0, 200)}`,
      );
    }
    await cloudAudit("employee", employeeId, "update",
      null, JSON.stringify({ inviteSent: true, email: parsed.email, level }));
    return { email: parsed.email ?? "" };
  },

  // Legacy name kept as a thin compat shim so any stale callers still
  // work — routes the same way as sendInvite.
  recordInviteSent: async (employeeId: string): Promise<{ email: string }> =>
    api.sendInvite(employeeId),

  // ------------- Admin set password (bypass invite email) -------------
  // Some agencies' email security scanners (Microsoft Defender,
  // Mimecast, Proofpoint, …) prefetch links to scan for phishing,
  // which burns the one-time invite token before the user can click
  // it. Supabase then redirects with "Error confirming user" on every
  // subsequent click and the invitee is stuck. This path lets a
  // supervisor stamp a password directly on the auth record so the
  // user can sign in without ever touching the invite email.
  //
  // The supervisor is expected to communicate the temporary password
  // out-of-band (Signal, phone, in person) — the response carries it
  // back so the supervisor sees it once and can pass it along.
  setEmployeePassword: async (employeeId: string, password?: string):
    Promise<{ email: string; password: string }> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Not signed in");
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const res = await fetch(`${supabaseUrl}/functions/v1/admin-set-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: anonKey ?? "",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ employeeId, ...(password ? { password } : {}) }),
    });
    const text = await res.text();
    let parsed: { ok?: boolean; email?: string; password?: string; error?: string } = {};
    try { parsed = JSON.parse(text); } catch { /* non-JSON */ }
    if (!res.ok) {
      throw new Error(
        parsed.error
          ?? `Set password failed (HTTP ${res.status}). ${text.slice(0, 200)}`,
      );
    }
    await cloudAudit("employee", employeeId, "update",
      null, JSON.stringify({ adminSetPassword: true, email: parsed.email }));
    return { email: parsed.email ?? "", password: parsed.password ?? "" };
  },

  // ------------- Departments (Supabase) -------------
  listDepartments: async (): Promise<Department[]> => {
    const { data, error } = await supabase.from("departments")
      .select("*").order("sort_order", { ascending: true });
    if (error) throw error;
    return fromSnakeList<Department>(data);
  },
  getDefaultDepartment: async (): Promise<Department> => {
    const { data, error } = await supabase.from("departments")
      .select("*").order("sort_order", { ascending: true }).limit(1).single();
    if (error) throw error;
    return fromSnake<Department>(data) as Department;
  },
  createDepartment: async (name: string): Promise<Department> => {
    const trimmed = name.trim();
    if (!trimmed) throw new Error("Department name required");
    const agencyId = await cloudAgencyId();
    const { count } = await supabase.from("departments")
      .select("*", { count: "exact", head: true });
    const { data, error } = await supabase.from("departments")
      .insert({ agency_id: agencyId, name: trimmed, sort_order: count ?? 0 })
      .select("*").single();
    if (error) throw error;
    const row = fromSnake<Department>(data) as Department;
    await cloudAudit("department", row.id, "create", null, JSON.stringify(row));
    return row;
  },
  updateDepartment: async (id: string, name: string): Promise<Department> => {
    const trimmed = name.trim();
    if (!trimmed) throw new Error("Department name required");
    const { data, error } = await supabase.from("departments")
      .update({ name: trimmed }).eq("id", id).select("*").single();
    if (error) throw error;
    const row = fromSnake<Department>(data) as Department;
    await cloudAudit("department", id, "update", null, JSON.stringify(row));
    return row;
  },
  // Cheap lookup for the Delete-department modal: how many rows in
  // other tables point at this department. Used to warn the user
  // and pick a reassignment target before cascade.
  countDepartmentDependencies: async (id: string): Promise<{
    empCount: number; shiftCount: number;
  }> => {
    const { count: empCount } = await supabase.from("employees")
      .select("*", { count: "exact", head: true }).eq("department_id", id);
    const { count: shiftCount } = await supabase.from("shifts")
      .select("*", { count: "exact", head: true }).eq("department_id", id);
    return { empCount: empCount ?? 0, shiftCount: shiftCount ?? 0 };
  },
  // Delete a department. When `reassignTo` is provided, move any
  // employees and shifts that point at this department onto that
  // target first — otherwise the ON DELETE CASCADE on the FKs would
  // wipe them out. Throws if the department still has dependents
  // and no target was given, so the UI can open the warn-and-pick
  // flow rather than silently losing data.
  deleteDepartment: async (
    id: string,
    opts?: { reassignTo?: string },
  ): Promise<void> => {
    const { count: total } = await supabase.from("departments")
      .select("*", { count: "exact", head: true });
    if ((total ?? 0) <= 1) throw new Error("Keep at least one department");
    const { empCount, shiftCount } = await api.countDepartmentDependencies(id);
    if (empCount > 0 || shiftCount > 0) {
      if (!opts?.reassignTo) {
        throw new Error(
          `This department still has ${empCount} employee(s) and ${shiftCount} shift(s) attached. Pick a department to move them to first.`,
        );
      }
      if (opts.reassignTo === id) {
        throw new Error("Pick a different department to move rows to.");
      }
      if (empCount > 0) {
        const { error: e1 } = await supabase.from("employees")
          .update({ department_id: opts.reassignTo }).eq("department_id", id);
        if (e1) throw e1;
      }
      if (shiftCount > 0) {
        const { error: e2 } = await supabase.from("shifts")
          .update({ department_id: opts.reassignTo }).eq("department_id", id);
        if (e2) throw e2;
      }
    }
    const { data: prior } = await supabase.from("departments")
      .select("*").eq("id", id).maybeSingle();
    const { error } = await supabase.from("departments").delete().eq("id", id);
    if (error) throw error;
    await cloudAudit("department", id, "delete",
      prior ? JSON.stringify(prior) : null, null);
  },

  // ------------- Users / accounts (Supabase profiles) -------------
  listUsers: async (): Promise<User[]> => {
    const { data, error } = await supabase.from("profiles")
      .select("id, agency_id, username, email, level, employee_id, accepted_at, disabled_at, is_beta_tester")
      .order("username", { ascending: true });
    if (error) throw error;
    return (data ?? []).map((p) => ({
      id: p.id as string,
      agencyId: p.agency_id as string,
      username: (p.username as string | null) ?? (p.email as string | null)?.split("@")[0] ?? "user",
      email: (p.email as string | null) ?? "",
      passwordHash: null,
      passwordSalt: null,
      level: p.level as UserLevel,
      role: p.level === "owner" ? "admin"
          : p.level === "l2" ? "supervisor"
          : "employee",
      employeeId: (p.employee_id as string | null) ?? null,
      acceptedAt: (p.accepted_at as string | null) ?? null,
      disabledAt: (p.disabled_at as string | null) ?? null,
      isBetaTester: (p.is_beta_tester as boolean | null) === true,
    }));
  },

  // Developer-only toggle to grant / revoke a user's beta-tester
  // flag. Routed through the SECURITY DEFINER helper set_beta_tester
  // in migration 0052, which double-checks the caller is the
  // platform developer server-side. Any other caller sees a DB
  // exception.
  setBetaTester: async (profileId: string, value: boolean): Promise<void> => {
    const { error } = await supabase.rpc("set_beta_tester", {
      p_profile_id: profileId,
      p_value: value,
    });
    if (error) throw new Error(error.message);
  },
  createUser: async (_input: { username: string; level: UserLevel }): Promise<User> => {
    void _input;
    throw new Error(
      "Create accounts by sending an invite from an employee profile. The account is created automatically once the invitee accepts the email.",
    );
  },
  updateUser: async (id: string, patch: Partial<User>): Promise<User> => {
    if (patch.level === "owner") {
      throw new Error("Use transferOwnership() to change the owner.");
    }
    const update: Record<string, unknown> = {};
    if (patch.username !== undefined) update.username = patch.username;
    if (patch.level !== undefined) update.level = patch.level;
    if (patch.disabledAt !== undefined) update.disabled_at = patch.disabledAt;
    if (patch.employeeId !== undefined) update.employee_id = patch.employeeId;
    const { data, error } = await supabase.from("profiles")
      .update(update).eq("id", id).select("*").single();
    if (error) throw error;
    await cloudAudit("user", id, "update", null, JSON.stringify(update));
    const p = data as Record<string, unknown>;
    return {
      id: p.id as string,
      agencyId: p.agency_id as string,
      username: (p.username as string | null) ?? "",
      email: (p.email as string | null) ?? "",
      passwordHash: null,
      passwordSalt: null,
      level: p.level as UserLevel,
      role: p.level === "owner" ? "admin"
          : p.level === "l2" ? "supervisor"
          : "employee",
      employeeId: (p.employee_id as string | null) ?? null,
      acceptedAt: (p.accepted_at as string | null) ?? null,
      disabledAt: (p.disabled_at as string | null) ?? null,
      isBetaTester: (p.is_beta_tester as boolean | null) === true,
    };
  },
  deleteUser: async (id: string): Promise<void> => {
    // Soft-disable; actual auth.users deletion requires admin privileges
    // and is done from Supabase Dashboard or the send-invite function
    // once we have one. This flips disabled_at so login is blocked.
    const { data: prior } = await supabase.from("profiles")
      .select("*").eq("id", id).maybeSingle();
    const { error } = await supabase.from("profiles")
      .update({ disabled_at: new Date().toISOString() }).eq("id", id);
    if (error) throw error;
    await cloudAudit("user", id, "delete",
      prior ? JSON.stringify(prior) : null, null);
  },
  // ----------------------------------------------------------------
  // Account-deletion request flow (Apple Guideline 5.1.1(v))
  // ----------------------------------------------------------------
  // Officers can't unilaterally delete themselves because the agency
  // owns their schedule + records-retention data. Instead they file
  // a request here; L1+ users see it in Settings and approve / deny.
  // Approve calls api.deleteUser (soft-archive — flips disabled_at).

  // Officer side: submit a deletion request for the signed-in user.
  // The migration's partial unique index prevents stacking pending
  // requests, so a duplicate submit returns the existing row instead
  // of inserting a second.
  requestAccountDeletion: async (reason?: string): Promise<DeletionRequest> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error("Not signed in.");
    const agencyId = await cloudAgencyId();
    // Check for an existing pending request first to give a clean
    // "you already filed one" rather than a unique-violation error.
    const { data: existing } = await supabase
      .from("deletion_requests")
      .select("*")
      .eq("user_id", session.user.id)
      .eq("status", "pending")
      .maybeSingle();
    if (existing) return mapDeletionRequest(existing);
    const { data, error } = await supabase
      .from("deletion_requests")
      .insert({
        user_id: session.user.id,
        agency_id: agencyId,
        reason: reason?.trim() || null,
      })
      .select("*")
      .single();
    if (error) throw new Error(error.message);

    // Fire-and-forget notification to supervisors so they see the
    // request even if they don't open Settings. notifySupervisors
    // catches its own errors so a notify failure can't block the
    // request submission.
    const u = await cloudCurrentUser();
    const who = u?.username ?? u?.email ?? "An officer";
    await api.notifySupervisors(
      "Account deletion requested",
      `${who} has requested that their ShiftManager account be deleted.`
      + (reason ? `\n\nReason: ${reason}` : "")
      + `\n\nReview and act on the request in Settings → Account deletion requests.`,
    );

    await cloudAudit("user", session.user.id, "create",
      null, JSON.stringify({ event: "deletion_request", reason: reason ?? null }));
    return mapDeletionRequest(data);
  },

  // Officer side: the user's currently-open request, if any. Used to
  // disable the Request button while one is pending.
  getMyDeletionRequest: async (): Promise<DeletionRequest | null> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;
    const { data } = await supabase
      .from("deletion_requests")
      .select("*")
      .eq("user_id", session.user.id)
      .eq("status", "pending")
      .maybeSingle();
    return data ? mapDeletionRequest(data) : null;
  },

  // Officer side: walk back a pending request before a supervisor has
  // acted on it.
  cancelMyDeletionRequest: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from("deletion_requests")
      .update({ status: "canceled", resolved_at: new Date().toISOString() })
      .eq("id", id)
      .eq("status", "pending");
    if (error) throw new Error(error.message);
  },

  // Supervisor side: list every pending request in this agency, plus
  // the most recent resolved ones (cap 50) for context.
  listDeletionRequests: async (): Promise<DeletionRequest[]> => {
    const { data, error } = await supabase
      .from("deletion_requests")
      .select("*")
      .order("requested_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return (data ?? []).map(mapDeletionRequest);
  },

  // Supervisor side: approve a request. Atomic-ish: mark resolved,
  // then call the existing soft-archive deleteUser. If the archive
  // step throws we roll the request back to pending so the
  // supervisor can retry — the audit log will record both attempts.
  approveDeletionRequest: async (id: string, note?: string): Promise<void> => {
    const { data: req, error: readErr } = await supabase
      .from("deletion_requests")
      .select("*").eq("id", id).maybeSingle();
    if (readErr) throw new Error(readErr.message);
    if (!req) throw new Error("Request not found.");
    if (req.status !== "pending") {
      throw new Error("Request has already been resolved.");
    }
    const { data: { session } } = await supabase.auth.getSession();
    const { error: stampErr } = await supabase
      .from("deletion_requests")
      .update({
        status: "approved",
        resolved_at: new Date().toISOString(),
        resolved_by: session?.user.id ?? null,
        resolved_note: note?.trim() || null,
      })
      .eq("id", id)
      .eq("status", "pending");
    if (stampErr) throw new Error(stampErr.message);
    try {
      await api.deleteUser(req.user_id);
    } catch (e) {
      // Rollback so the supervisor sees the request still pending.
      await supabase.from("deletion_requests")
        .update({ status: "pending", resolved_at: null, resolved_by: null })
        .eq("id", id);
      throw e;
    }
    // Tell the user their request was approved (best-effort — by this
    // point their account is disabled so they can't sign in to read
    // it anyway, but the inbox row + email reach them).
    await api.notifyUser(
      req.user_id,
      "Account deletion approved",
      "Your ShiftManager account has been deleted per your request."
      + (note ? `\n\nNote: ${note}` : ""),
    );
  },

  // Supervisor side: deny a request with a required note explaining
  // why (so the officer knows what to do next — usually a policy
  // pointer).
  denyDeletionRequest: async (id: string, note: string): Promise<void> => {
    if (!note?.trim()) throw new Error("Please include a reason when denying.");
    const { data: req, error: readErr } = await supabase
      .from("deletion_requests")
      .select("*").eq("id", id).maybeSingle();
    if (readErr) throw new Error(readErr.message);
    if (!req) throw new Error("Request not found.");
    if (req.status !== "pending") {
      throw new Error("Request has already been resolved.");
    }
    const { data: { session } } = await supabase.auth.getSession();
    const { error } = await supabase
      .from("deletion_requests")
      .update({
        status: "denied",
        resolved_at: new Date().toISOString(),
        resolved_by: session?.user.id ?? null,
        resolved_note: note.trim(),
      })
      .eq("id", id)
      .eq("status", "pending");
    if (error) throw new Error(error.message);
    await api.notifyUser(
      req.user_id,
      "Account deletion denied",
      `Your ShiftManager account deletion request was denied.\n\nReason: ${note.trim()}`,
    );
  },

  // Transfer ownership via the Postgres SECURITY DEFINER function from
  // supabase/migrations/0001_initial.sql.
  transferOwnership: async (args: {
    fromUserId: string;
    toUserId: string;
    confirmation: string;
  }): Promise<void> => {
    if (args.confirmation !== OWNERSHIP_SENTENCE) {
      throw new Error("Confirmation sentence does not match.");
    }
    const { error } = await supabase.rpc("transfer_ownership", {
      new_owner: args.toUserId,
      confirmation: args.confirmation,
    });
    if (error) throw new Error(error.message);
    await cloudAudit("user", args.toUserId, "update",
      null, JSON.stringify({ level: "owner" }));
  },

  // Legacy no-op in the cloud world — Supabase has exactly one session.
  setActingUser: async (_userId: string | null): Promise<void> => {
    void _userId;
  },

  // ------------- Employees (Supabase) -------------
  listEmployees: async (opts?: { departmentId?: string }): Promise<Employee[]> => {
    let q = supabase.from("employees").select("*")
      .is("deleted_at", null)
      .order("last_name", { ascending: true });
    if (opts?.departmentId) q = q.eq("department_id", opts.departmentId);
    const { data, error } = await q;
    if (error) throw error;
    return fromSnakeList<Employee>(data);
  },

  createEmployee: async (input: Partial<Employee>): Promise<Employee> => {
    if (!input.firstName?.trim() || !input.lastName?.trim()) {
      throw new Error("First and last name required");
    }
    const agencyId = await cloudAgencyId();
    let deptId = input.departmentId;
    if (!deptId) {
      deptId = (await api.getDefaultDepartment()).id;
    }
    const row = {
      agency_id: agencyId,
      department_id: deptId,
      first_name: input.firstName,
      last_name: input.lastName,
      badge_number: input.badgeNumber ?? null,
      radio_number: input.radioNumber ?? null,
      rank: input.rank ?? null,
      hire_date: input.hireDate ?? null,
      retirement_date: input.retirementDate ?? null,
      status: (input.status as EmployeeStatus) ?? "active",
      is_oic: input.isOic ?? false,
      is_fto: input.isFto ?? false,
      level: input.level ?? "l4",
      emails: input.emails ?? [],
      phones: input.phones ?? [],
      invited_at: null as string | null,
      home_shift_id: input.homeShiftId ?? null,
      home_unit_id: input.homeUnitId ?? null,
      days_off_weekdays: input.daysOffWeekdays ?? [],
      days_off_weekdays_w2: input.daysOffWeekdaysW2 ?? null,
      tac_days_off_weekdays: input.tacDaysOffWeekdays ?? [],
      tac_days_off_weekdays_w2: input.tacDaysOffWeekdaysW2 ?? null,
      days_off_mode: input.daysOffMode ?? "regular",
      days_off_start_date: input.daysOffStartDate ?? null,
      follows_tac_mode: input.followsTacMode ?? false,
      tac_home_shift_id: input.tacHomeShiftId ?? null,
      notes: input.notes ?? null,
      photo: input.photo ?? null,
    };
    const { data, error } = await supabase.from("employees")
      .insert(row).select("*").single();
    if (error) throw error;
    const saved = fromSnake<Employee>(data) as Employee;
    await cloudAudit("employee", saved.id, "create", null, JSON.stringify(saved));
    return saved;
  },

  updateEmployee: async (id: string, input: Partial<Employee>): Promise<Employee> => {
    const patch: Record<string, unknown> = {};
    const map: Record<keyof Employee, string> = {
      id: "id",
      agencyId: "agency_id",
      departmentId: "department_id",
      firstName: "first_name",
      lastName: "last_name",
      badgeNumber: "badge_number",
      radioNumber: "radio_number",
      rank: "rank",
      hireDate: "hire_date",
      retirementDate: "retirement_date",
      status: "status",
      isOic: "is_oic",
      isFto: "is_fto",
      level: "level",
      emails: "emails",
      phones: "phones",
      invitedAt: "invited_at",
      homeShiftId: "home_shift_id",
      homeUnitId: "home_unit_id",
      daysOffWeekdays: "days_off_weekdays",
      daysOffWeekdaysW2: "days_off_weekdays_w2",
      tacDaysOffWeekdays: "tac_days_off_weekdays",
      tacDaysOffWeekdaysW2: "tac_days_off_weekdays_w2",
      daysOffMode: "days_off_mode",
      daysOffStartDate: "days_off_start_date",
      followsTacMode: "follows_tac_mode",
      tacHomeShiftId: "tac_home_shift_id",
      notes: "notes",
      photo: "photo",
      deletedAt: "deleted_at",
    };
    for (const [k, col] of Object.entries(map) as [keyof Employee, string][]) {
      if (k === "id" || k === "agencyId") continue;
      if (input[k] !== undefined) patch[col] = input[k];
    }
    const { data, error } = await supabase.from("employees")
      .update(patch).eq("id", id).select("*").single();
    if (error) throw error;
    const saved = fromSnake<Employee>(data) as Employee;
    await cloudAudit("employee", id, "update", null, JSON.stringify(saved));
    return saved;
  },

  deleteEmployee: async (id: string): Promise<void> => {
    const { data: prior } = await supabase.from("employees")
      .select("*").eq("id", id).maybeSingle();
    const { error } = await supabase.from("employees")
      .update({ deleted_at: new Date().toISOString() }).eq("id", id);
    if (error) throw error;
    await cloudAudit("employee", id, "delete",
      prior ? JSON.stringify(prior) : null, null);
  },

  // ------------- Shifts (Supabase) -------------
  listShifts: async (opts?: { departmentId?: string }): Promise<Shift[]> => {
    let q = supabase.from("shifts").select("*")
      .is("archived_at", null)
      .order("sort_order", { ascending: true });
    if (opts?.departmentId) q = q.eq("department_id", opts.departmentId);
    const { data, error } = await q;
    if (error) throw error;
    return fromSnakeList<Shift>(data);
  },

  createShift: async (input: Partial<Shift>): Promise<Shift> => {
    if (!input.durationMin || input.durationMin <= 0 || input.durationMin > 24 * 60) {
      throw new Error("Duration must be between 1 and 1440 minutes");
    }
    const agencyId = await cloudAgencyId();
    // Count existing active shifts so the new row's sort_order
    // lands at the end. No ceiling — agencies with multiple
    // departments run well over a dozen shifts (patrol, detectives,
    // traffic, SRO, dispatch, reserves, …).
    const { count: active } = await supabase.from("shifts")
      .select("*", { count: "exact", head: true }).is("archived_at", null);
    const deptId = input.departmentId ?? (await api.getDefaultDepartment()).id;
    const row = {
      agency_id: agencyId,
      department_id: deptId,
      name: input.name ?? "New shift",
      start_time: input.startTime ?? "06:00",
      duration_min: input.durationMin,
      color: input.color ?? "#1e6bff",
      min_staffing: input.minStaffing ?? 1,
      slot_count: input.slotCount ?? 8,
      sort_order: input.sortOrder ?? (active ?? 0),
      in_rotation: input.inRotation ?? true,
      requires_oic: input.requiresOic ?? true,
      tac_role: input.tacRole ?? "regular",
      overtime_windows: input.overtimeWindows ?? [],
      coverage_inactive_weekdays: input.coverageInactiveWeekdays ?? [],
      presence_coverage: input.presenceCoverage ?? false,
    };
    const { data, error } = await supabase.from("shifts").insert(row)
      .select("*").single();
    if (error) throw error;
    const saved = fromSnake<Shift>(data) as Shift;
    await cloudAudit("shift", saved.id, "create", null, JSON.stringify(saved));
    return saved;
  },

  updateShift: async (id: string, input: Partial<Shift>): Promise<Shift> => {
    const patch: Record<string, unknown> = {};
    if (input.name !== undefined) patch.name = input.name;
    if (input.startTime !== undefined) patch.start_time = input.startTime;
    if (input.durationMin !== undefined) patch.duration_min = input.durationMin;
    if (input.color !== undefined) patch.color = input.color;
    if (input.minStaffing !== undefined) patch.min_staffing = input.minStaffing;
    if (input.slotCount !== undefined) patch.slot_count = input.slotCount;
    if (input.sortOrder !== undefined) patch.sort_order = input.sortOrder;
    if (input.departmentId !== undefined) patch.department_id = input.departmentId;
    if (input.inRotation !== undefined) patch.in_rotation = input.inRotation;
    if (input.requiresOic !== undefined) patch.requires_oic = input.requiresOic;
    if (input.tacRole !== undefined) patch.tac_role = input.tacRole;
    if (input.overtimeWindows !== undefined) patch.overtime_windows = input.overtimeWindows;
    if (input.coverageInactiveWeekdays !== undefined) {
      patch.coverage_inactive_weekdays = input.coverageInactiveWeekdays;
    }
    if (input.presenceCoverage !== undefined) {
      patch.presence_coverage = input.presenceCoverage;
    }
    const { data, error } = await supabase.from("shifts")
      .update(patch).eq("id", id).select("*").single();
    if (error) throw error;
    const saved = fromSnake<Shift>(data) as Shift;
    await cloudAudit("shift", id, "update", null, JSON.stringify(saved));
    return saved;
  },

  setShiftSlotCount: async (id: string, slotCount: number): Promise<Shift> => {
    const clamped = Math.max(1, Math.min(50, Math.round(slotCount)));
    const { data, error } = await supabase.from("shifts")
      .update({ slot_count: clamped }).eq("id", id).select("*").single();
    if (error) throw error;
    const saved = fromSnake<Shift>(data) as Shift;
    await cloudAudit("shift", id, "update", null,
      JSON.stringify({ slotCount: clamped }));
    return saved;
  },

  archiveShift: async (id: string): Promise<void> => {
    // Snapshot the row so the audit log keeps the shift name —
    // the live shifts list filters out archived rows, so without
    // this the Target column collapses to a UUID prefix.
    const { data: prior } = await supabase.from("shifts")
      .select("*").eq("id", id).maybeSingle();
    const { error } = await supabase.from("shifts")
      .update({ archived_at: new Date().toISOString() }).eq("id", id);
    if (error) throw error;
    await cloudAudit("shift", id, "delete",
      prior ? JSON.stringify(prior) : null, null);
  },

  // Reverse of archiveShift — clears archived_at so the shift
  // comes back to the live roster. Existing assignments still
  // reference the row (cascade-deletes never fired), so the
  // schedule history reappears as soon as the shift is back.
  unarchiveShift: async (id: string): Promise<void> => {
    const { data: row, error } = await supabase.from("shifts")
      .update({ archived_at: null }).eq("id", id).select("*").single();
    if (error) throw error;
    const saved = fromSnake<Shift>(row) as Shift;
    await cloudAudit("shift", id, "update", null, JSON.stringify(saved));
  },

  // Hard delete of an archived shift. Cascades to assignments
  // (FK is ON DELETE CASCADE) and nulls out home_shift_id on any
  // employee that was pointing at it. Destructive — the UI gates
  // this behind a count-and-confirm flow that surfaces how many
  // assignment rows will go away.
  listArchivedShifts: async (): Promise<Shift[]> => {
    const { data, error } = await supabase.from("shifts")
      .select("*")
      .not("archived_at", "is", null)
      .order("archived_at", { ascending: false });
    if (error) throw error;
    return fromSnakeList<Shift>(data);
  },
  countShiftAssignments: async (id: string): Promise<number> => {
    const { count } = await supabase.from("assignments")
      .select("*", { count: "exact", head: true })
      .eq("shift_id", id);
    return count ?? 0;
  },
  deleteShift: async (id: string): Promise<void> => {
    // Snapshot for the audit row — once the FK cascades, the
    // shift's name is gone for good.
    const { data: prior } = await supabase.from("shifts")
      .select("*").eq("id", id).maybeSingle();
    const { error } = await supabase.from("shifts").delete().eq("id", id);
    if (error) throw error;
    await cloudAudit("shift", id, "delete",
      prior ? JSON.stringify(prior) : null, null);
  },

  // ------------- Shift assignments (temporary date-range overrides) -------------
  // Officer X normally works Days but is filling in on Nights from
  // June 12 → June 25 — we record that as one shift_assignments
  // row. The schedule grid projects them onto Nights for those
  // dates while their homeShiftId stays Days for everything else.
  listShiftAssignments: async (
    startDate?: string, endDate?: string,
  ): Promise<ShiftAssignment[]> => {
    let q = supabase.from("shift_assignments").select("*")
      .order("start_date", { ascending: true });
    // Range filter: keep rows whose window overlaps the requested
    // window. A row that starts after endDate or ends before
    // startDate can't possibly affect the visible schedule.
    if (endDate) q = q.lte("start_date", endDate);
    if (startDate) q = q.gte("end_date", startDate);
    const { data, error } = await q;
    if (error) throw error;
    return fromSnakeList<ShiftAssignment>(data);
  },

  createShiftAssignment: async (input: {
    employeeId: string;
    shiftId: string;
    startDate: string;
    endDate: string;
    note?: string | null;
  }): Promise<ShiftAssignment> => {
    const agencyId = await cloudAgencyId();
    const { data, error } = await supabase.from("shift_assignments").insert({
      agency_id: agencyId,
      employee_id: input.employeeId,
      shift_id: input.shiftId,
      start_date: input.startDate,
      end_date: input.endDate,
      note: input.note ?? null,
    }).select("*").single();
    if (error) throw error;
    const saved = fromSnake<ShiftAssignment>(data) as ShiftAssignment;
    await cloudAudit("shift_assignment", saved.id, "create",
      null, JSON.stringify(saved));
    return saved;
  },

  deleteShiftAssignment: async (id: string): Promise<void> => {
    const { data: prior } = await supabase.from("shift_assignments")
      .select("*").eq("id", id).maybeSingle();
    const { error } = await supabase.from("shift_assignments").delete().eq("id", id);
    if (error) throw error;
    await cloudAudit("shift_assignment", id, "delete",
      prior ? JSON.stringify(prior) : null, null);
  },

  // ------------- IOD periods -------------
  // List / create / delete per-officer Injury-On-Duty periods.
  // Materialisation onto the schedule happens lazily through
  // applyDaysOffPattern; the grid also overlays IOD chips on
  // UNASSIGNED rows directly so an officer with no shift still
  // shows their IOD range without needing assignment rows.
  listIodPeriods: async (
    startDate?: string, endDate?: string,
  ): Promise<IodPeriod[]> => {
    let q = supabase.from("iod_periods").select("*")
      .order("start_date", { ascending: true });
    // Range filter: only return rows that overlap [startDate, endDate]
    if (endDate) q = q.lte("start_date", endDate);
    // end_date null means open-ended, always overlaps.
    if (startDate) q = q.or(`end_date.gte.${startDate},end_date.is.null`);
    const { data, error } = await q;
    if (error) throw error;
    return fromSnakeList<IodPeriod>(data);
  },

  createIodPeriod: async (input: {
    employeeId: string;
    startDate: string;
    endDate: string | null;
    note?: string | null;
  }): Promise<IodPeriod> => {
    const agencyId = await cloudAgencyId();
    const { data: { session } } = await supabase.auth.getSession();
    const { data, error } = await supabase.from("iod_periods").insert({
      agency_id: agencyId,
      employee_id: input.employeeId,
      start_date: input.startDate,
      end_date: input.endDate,
      note: input.note ?? null,
      set_by: session?.user?.id ?? null,
    }).select("*").single();
    if (error) throw error;
    const saved = fromSnake<IodPeriod>(data) as IodPeriod;
    await cloudAudit("iod_period", saved.id, "create",
      null, JSON.stringify(saved));
    return saved;
  },

  deleteIodPeriod: async (id: string): Promise<void> => {
    const { data: prior } = await supabase.from("iod_periods")
      .select("*").eq("id", id).maybeSingle();
    const { error } = await supabase.from("iod_periods").delete().eq("id", id);
    if (error) throw error;
    await cloudAudit("iod_period", id, "delete",
      prior ? JSON.stringify(prior) : null, null);
  },

  // ------------- Clear procedural shifts -------------
  // Wipe an officer's procedural-shift state going forward:
  //   * Delete every from_pattern=true assignment dated >= fromDate
  //     (the materialised XXX rows). Manual edits — supervisor-
  //     typed TR / S / V / OT etc. — are from_pattern=false and
  //     survive untouched.
  //   * Null out homeShiftId and clear every days-off-pattern
  //     field so applyDaysOffPattern is a no-op next time the
  //     employee is saved.
  //
  // Past rows (date < fromDate) are preserved so historical
  // reporting and the schedule's "look back" still read accurately.
  clearProceduralShifts: async (input: {
    employeeId: string;
    fromDate: string;             // YYYY-MM-DD inclusive
  }): Promise<{ deletedRows: number }> => {
    const { data: priorEmp } = await supabase.from("employees")
      .select("*").eq("id", input.employeeId).maybeSingle();
    const { data: deleted, error: delErr } = await supabase
      .from("assignments")
      .delete()
      .eq("employee_id", input.employeeId)
      .eq("from_pattern", true)
      .gte("date", input.fromDate)
      .select("id");
    if (delErr) throw delErr;
    const { error: updErr } = await supabase.from("employees")
      .update({
        home_shift_id: null,
        days_off_weekdays: [],
        days_off_weekdays_w2: null,
        tac_days_off_weekdays: [],
        tac_days_off_weekdays_w2: null,
        days_off_start_date: null,
      })
      .eq("id", input.employeeId);
    if (updErr) throw updErr;
    await cloudAudit("employee", input.employeeId, "update",
      priorEmp ? JSON.stringify(priorEmp) : null,
      JSON.stringify({
        clearedProceduralShifts: input.fromDate,
        deletedAssignments: deleted?.length ?? 0,
      }));
    return { deletedRows: deleted?.length ?? 0 };
  },

  // ------------- Assignments (Supabase) -------------
  listAssignments: async (startDate: string, endDate: string): Promise<Assignment[]> => {
    const { data, error } = await supabase.from("assignments")
      .select("*")
      .gte("date", startDate).lte("date", endDate);
    if (error) throw error;
    return fromSnakeList<Assignment>(data);
  },

  createAssignment: async (input: {
    employeeId: string; shiftId: string; date: string;
    note?: string; overrideReason?: string; coverageGroupId?: string;
    otStartTime?: string; otEndTime?: string; fromPattern?: boolean;
  }): Promise<Assignment> => {
    const agencyId = await cloudAgencyId();

    // Conflict detection: same-day assignment on THIS shift + approved
    // leave. The uniqueness constraint the DB actually enforces is
    // (employee_id, shift_id, date), so only a same-shift row is a
    // real conflict — counting across all shifts blocks legitimate
    // grid edits when the officer already has cross-shift OT that
    // day (e.g. Days officer picking up 02:00-14:00 Nights OT can't
    // be marked sick on their home Days shift without hitting this
    // guard, which fires silently and leaves the cell unchanged).
    const { count: sameDay } = await supabase.from("assignments")
      .select("*", { count: "exact", head: true })
      .eq("employee_id", input.employeeId)
      .eq("shift_id", input.shiftId)
      .eq("date", input.date)
      .neq("status", "cancelled");
    const { data: leaveRows } = await supabase.from("time_off")
      .select("id").eq("employee_id", input.employeeId).eq("status", "approved")
      .lte("start_date", input.date).gte("end_date", input.date);
    const onLeave = (leaveRows?.length ?? 0) > 0;

    const override = input.overrideReason?.trim();
    if (((sameDay ?? 0) > 0 || onLeave) && !override) {
      throw new Error(
        onLeave
          ? "Employee on approved leave; provide an override reason."
          : "Employee already has an assignment that day; provide an override reason.",
      );
    }

    // Auto-join of complementary OT halves used to fire silently in
    // this function. Supervisors asked for explicit control instead
    // (they want to see a "Pair with [X]?" prompt after Fix Coverage
    // saves, not have chips silently combine), so the join now
    // happens client-side via api.mergeIntoCoverageGroup below.
    // Fresh OTs always start as their own solo coverage group.
    const resolvedCoverageGroupId = input.coverageGroupId ?? null;

    const { data, error } = await supabase.from("assignments").insert({
      agency_id: agencyId,
      employee_id: input.employeeId,
      shift_id: input.shiftId,
      date: input.date,
      status: "scheduled",
      note: input.note ?? null,
      override_reason: override ?? null,
      coverage_group_id: resolvedCoverageGroupId,
      ot_start_time: input.otStartTime ?? null,
      ot_end_time: input.otEndTime ?? null,
      from_pattern: input.fromPattern ?? false,
    }).select("*").single();
    if (error) throw error;
    const saved = fromSnake<Assignment>(data) as Assignment;
    // Stamp the audit snapshot with the raw snake_case row rather
    // than the camelCase Assignment so downstream filters (cell
    // history) can key off the same field names the DB uses. Older
    // rows on record still use camelCase; the listAudit filter
    // handles both variants.
    await cloudAudit("assignment", saved.id, "assign", null, JSON.stringify(data));
    // Notify the covering officer on OT rows. A split pair fires
    // two notifications (one per covering officer), which matches
    // the real-world action. Non-OT cell edits (upsertMut) don't
    // match this branch.
    if ((input.note ?? "").trim().toUpperCase() === "OT") {
      const { data: sh } = await supabase
        .from("shifts")
        .select("name")
        .eq("id", input.shiftId)
        .maybeSingle();
      const hours = input.otStartTime && input.otEndTime
        ? `${input.otStartTime}–${input.otEndTime}`
        : "full shift";
      void api.notifyEmployee(
        input.employeeId,
        `Overtime scheduled — ${sh?.name ?? "shift"} on ${input.date}`,
        `You've been scheduled for overtime coverage.\n\n`
        + `Shift: ${sh?.name ?? "(unknown)"}\n`
        + `Date: ${input.date}\n`
        + `Hours: ${hours}\n\n`
        + "If this is wrong, please contact your supervisor.",
      );
    }
    return saved;
  },

  // Delete every assignment row on a given (employee, shift, date)
  // cell. Used by FixCoverage to clear an off-code (XXX from the
  // days-off pattern, V, etc.) before stamping a fresh OT row —
  // the assignments table has a unique constraint on
  // (employee_id, shift_id, date) so the two can't coexist.
  // Returns how many rows were dropped so the caller can decide
  // whether to log it. Audit entries fire per deleted row.
  clearCellAssignments: async (input: {
    employeeId: string; shiftId: string; date: string;
  }): Promise<number> => {
    const { data: priors } = await supabase.from("assignments")
      .select("*")
      .eq("employee_id", input.employeeId)
      .eq("shift_id", input.shiftId)
      .eq("date", input.date)
      .neq("status", "cancelled");
    if (!priors || priors.length === 0) return 0;
    const ids = priors.map((p) => (p as { id: string }).id);
    const { error } = await supabase.from("assignments")
      .delete().in("id", ids);
    if (error) throw error;
    for (const p of priors) {
      await cloudAudit("assignment",
        (p as { id: string }).id, "unassign",
        JSON.stringify(p), null);
    }
    return priors.length;
  },

  deleteAssignment: async (id: string): Promise<void> => {
    // Capture the row before the hard delete so the audit log can
    // surface a useful Target label (officer · date · shift) in
    // place of an opaque 8-char UUID prefix.
    const { data: prior } = await supabase.from("assignments")
      .select("*").eq("id", id).maybeSingle();
    const { error } = await supabase.from("assignments").delete().eq("id", id);
    if (error) throw error;
    await cloudAudit("assignment", id, "unassign",
      prior ? JSON.stringify(prior) : null, null);
  },

  // Look for a "pair candidate" for a newly-created OT row on the
  // same (shift, date): a solo OT (its coverage_group_id has no
  // other members) whose worked hours do NOT overlap the new
  // row's. Overlapping hours would be a doubled seat, not a pair.
  // Returns the candidate row or null if none exists.
  //
  // Used by Fix Coverage's post-save pair-prompt so a supervisor
  // can explicitly attach the new OT to an existing solo, instead
  // of the older silent auto-join that was chaining 3-4 officers
  // into a single chip when successive supervisors added coverage.
  findOtPairCandidate: async (input: {
    shiftId: string; date: string;
    forAssignmentId: string;
    otStartTime: string; otEndTime: string;
  }): Promise<Assignment | null> => {
    const { data, error } = await supabase.from("assignments")
      .select("*")
      .eq("shift_id", input.shiftId)
      .eq("date", input.date)
      .eq("note", "OT")
      .neq("id", input.forAssignmentId)
      .neq("status", "cancelled")
      .not("coverage_group_id", "is", null);
    if (error) throw error;
    const rows = fromSnakeList<Assignment>(data);
    if (rows.length === 0) return null;
    // Solos only: skip rows whose coverage_group_id already has
    // multiple members (would push the group past a pair).
    const groupCount = new Map<string, number>();
    for (const r of rows) {
      if (!r.coverageGroupId) continue;
      groupCount.set(r.coverageGroupId, (groupCount.get(r.coverageGroupId) ?? 0) + 1);
    }
    const newS = hourOf(input.otStartTime);
    const newE = hourOf(input.otEndTime);
    if (newS === null || newE === null) return null;
    const newBuckets = new Set(bucketsBetween(newS, newE));
    for (const r of rows) {
      if (!r.coverageGroupId) continue;
      if ((groupCount.get(r.coverageGroupId) ?? 0) !== 1) continue;
      const s = hourOf(r.otStartTime);
      const e = hourOf(r.otEndTime);
      if (s === null || e === null) continue;
      const rowBuckets = bucketsBetween(s, e);
      if (rowBuckets.every((h) => !newBuckets.has(h))) return r;
    }
    return null;
  },

  // Attach an OT row to an existing coverage group so the pair
  // renders as one paired chip. Called from the Fix Coverage post-
  // save prompt when the supervisor picks "Yes, pair them".
  mergeIntoCoverageGroup: async (
    assignmentId: string, targetCoverageGroupId: string,
  ): Promise<Assignment> => {
    const { data, error } = await supabase.from("assignments")
      .update({ coverage_group_id: targetCoverageGroupId })
      .eq("id", assignmentId)
      .select("*").single();
    if (error) throw error;
    return fromSnake<Assignment>(data) as Assignment;
  },

  // ------------- Notifications (fire-and-forget) -------------
  //
  // All three helpers invoke the send-notification Edge Function,
  // which:
  //  - looks up the recipient's linked user row
  //  - skips anyone whose invite hasn't been accepted yet
  //    (password_hash is null)
  //  - sends via Resend (or logs if no RESEND_API_KEY env var)
  // Failures never surface to the caller — notifications are
  // supplementary, they shouldn't block core workflows.

  notifyEmployee: async (employeeId: string, subject: string, body: string) => {
    try {
      await supabase.functions.invoke("send-notification", {
        body: { to: { employeeId }, subject, body },
      });
    } catch (e) {
      console.warn("notifyEmployee failed (non-fatal):", e);
    }
  },
  notifyUser: async (userId: string, subject: string, body: string) => {
    try {
      await supabase.functions.invoke("send-notification", {
        body: { to: { userId }, subject, body },
      });
    } catch (e) {
      console.warn("notifyUser failed (non-fatal):", e);
    }
  },

  // Beta-tester feedback — fires the user's note to the platform
  // developer's inbox via send-notification (email-only
  // destination, so there's no auth user needed on the receiving
  // end). Subject carries the sender's identity so the dev can
  // reply directly without opening the app.
  sendBetaFeedback: async (note: string): Promise<void> => {
    const trimmed = note.trim();
    if (!trimmed) throw new Error("Feedback can't be blank.");
    const { data: { session } } = await supabase.auth.getSession();
    const email = session?.user?.email ?? "unknown";
    const subject = `Beta feedback from ${email}`;
    const body =
      `${trimmed}\n\n`
      + `— Sent from the in-app Beta feedback button.\n`
      + `From: ${email}\n`;
    const { error } = await supabase.functions.invoke("send-notification", {
      body: {
        to: { email: "jamesleonbond@gmail.com" },
        subject,
        body,
      },
    });
    if (error) throw new Error(error.message ?? "Couldn't send feedback.");
  },
  // Inbox: fetch the signed-in user's most recent notifications.
  // RLS in 0041_notifications constrains the row set to the caller's
  // own user_id so a raw `select *` is safe — the policy does the
  // filtering server-side. Capped at 100 because the UI is a popover
  // and beyond that the user wants a real page (not built yet).
  listNotifications: async (): Promise<NotificationItem[]> => {
    const { data, error } = await supabase
      .from("notifications")
      .select("id, subject, body, kind, emailed, pushed, read_at, created_at")
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => ({
      id: r.id,
      subject: r.subject,
      body: r.body,
      kind: r.kind,
      emailed: r.emailed,
      pushed: r.pushed,
      readAt: r.read_at,
      createdAt: r.created_at,
    }));
  },

  // Stamp a single notification as read. The "update own
  // notifications" RLS policy means a misbehaving client can't read-
  // stamp someone else's row even if they guess the UUID.
  markNotificationRead: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("id", id)
      .is("read_at", null);
    if (error) console.warn("markNotificationRead failed:", error.message);
  },

  // Stamp every unread notification owned by the caller. Cheaper than
  // looping client-side and lets Postgres batch the writes. RLS still
  // scopes the update to the caller's own rows.
  markAllNotificationsRead: async (): Promise<void> => {
    const { error } = await supabase
      .from("notifications")
      .update({ read_at: new Date().toISOString() })
      .is("read_at", null);
    if (error) console.warn("markAllNotificationsRead failed:", error.message);
  },

  // -------- Notification forwarding --------
  //
  // The signed-in user opts IN to receive copies of another user's
  // notifications. RLS constrains listing / inserting / deleting to
  // rows where to_user_id = auth.uid(), so a caller can only wire
  // themselves as a recipient — never the reverse.
  listMyForwards: async (): Promise<Array<{
    id: string;
    fromUserId: string;
    toUserId: string;
    createdAt: string;
  }>> => {
    const { data, error } = await supabase
      .from("notification_forwards")
      .select("id, from_user_id, to_user_id, created_at")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => ({
      id: r.id,
      fromUserId: r.from_user_id,
      toUserId: r.to_user_id,
      createdAt: r.created_at,
    }));
  },

  addMyForward: async (fromUserId: string): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error("Not signed in.");
    if (session.user.id === fromUserId) {
      throw new Error("You can't forward your own notifications to yourself.");
    }
    const { error } = await supabase
      .from("notification_forwards")
      .insert({ from_user_id: fromUserId, to_user_id: session.user.id });
    if (error) {
      if ((error as { code?: string }).code === "23505") {
        throw new Error("You're already receiving forwards from that user.");
      }
      throw new Error(error.message);
    }
  },

  removeMyForward: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from("notification_forwards")
      .delete()
      .eq("id", id);
    if (error) throw new Error(error.message);
  },

  // Cell notes: free-form supervisor notes attached to a specific
  // (employee, date) cell on the schedule. Rendered as chips at the
  // top of the code-picker popover. Rows carry created_by so the
  // "delete own or supervisor" RLS policy can gate the ×.
  listCellNotes: async (
    startDate: string, endDate: string,
  ): Promise<CellNote[]> => {
    const { data, error } = await supabase
      .from("cell_notes")
      .select("*")
      .gte("date", startDate)
      .lte("date", endDate)
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    return fromSnakeList<CellNote>(data);
  },
  addCellNote: async (
    employeeId: string, date: string, note: string,
  ): Promise<CellNote> => {
    const trimmed = note.trim();
    if (!trimmed) throw new Error("Note is empty.");
    const agencyId = await cloudAgencyId();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error("Not signed in.");
    const { data, error } = await supabase
      .from("cell_notes")
      .insert({
        agency_id: agencyId,
        employee_id: employeeId,
        date,
        note: trimmed,
        created_by: session.user.id,
      })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return fromSnake<CellNote>(data) as CellNote;
  },
  deleteCellNote: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from("cell_notes")
      .delete()
      .eq("id", id);
    if (error) throw new Error(error.message);
  },

  // -------- Shift bid ---------------------------------------------
  // Sheets are keyed by (department_id, start_date). getShiftBidSheet
  // returns the matching sheet if one exists, otherwise null. Use
  // upsertShiftBidSheet to create-if-missing.
  getShiftBidSheet: async (
    departmentId: string, startDate: string,
  ): Promise<ShiftBidSheet | null> => {
    const { data, error } = await supabase
      .from("shift_bid_sheets")
      .select("*")
      .eq("department_id", departmentId)
      .eq("start_date", startDate)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ? fromSnake<ShiftBidSheet>(data) as ShiftBidSheet : null;
  },
  upsertShiftBidSheet: async (
    departmentId: string, startDate: string,
  ): Promise<ShiftBidSheet> => {
    const agencyId = await cloudAgencyId();
    const { data: { session } } = await supabase.auth.getSession();
    const { data, error } = await supabase
      .from("shift_bid_sheets")
      .upsert({
        agency_id: agencyId,
        department_id: departmentId,
        start_date: startDate,
        created_by: session?.user?.id ?? null,
      }, { onConflict: "agency_id,department_id,start_date" })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return fromSnake<ShiftBidSheet>(data) as ShiftBidSheet;
  },
  listShiftBidRows: async (sheetId: string): Promise<ShiftBidRow[]> => {
    const { data, error } = await supabase
      .from("shift_bid_rows")
      .select("*")
      .eq("sheet_id", sheetId)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    return fromSnakeList<ShiftBidRow>(data);
  },
  addShiftBidRow: async (input: {
    sheetId: string;
    shiftId: string | null;
    sortOrder?: number;
  }): Promise<ShiftBidRow> => {
    const { data, error } = await supabase
      .from("shift_bid_rows")
      .insert({
        sheet_id: input.sheetId,
        shift_id: input.shiftId,
        sort_order: input.sortOrder ?? 0,
        off_dates: [],
      })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return fromSnake<ShiftBidRow>(data) as ShiftBidRow;
  },
  updateShiftBidRow: async (
    id: string,
    patch: Partial<Pick<ShiftBidRow, "offDates" | "sortOrder" | "shiftId">>,
  ): Promise<ShiftBidRow> => {
    const upd: Record<string, unknown> = {};
    if (patch.offDates !== undefined) upd.off_dates = patch.offDates;
    if (patch.sortOrder !== undefined) upd.sort_order = patch.sortOrder;
    if (patch.shiftId !== undefined) upd.shift_id = patch.shiftId;
    const { data, error } = await supabase
      .from("shift_bid_rows")
      .update(upd)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return fromSnake<ShiftBidRow>(data) as ShiftBidRow;
  },
  deleteShiftBidRow: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from("shift_bid_rows")
      .delete()
      .eq("id", id);
    if (error) throw new Error(error.message);
  },
  claimShiftBidRow: async (
    rowId: string, employeeId?: string,
  ): Promise<ShiftBidRow> => {
    const { data, error } = await supabase.rpc("claim_shift_bid_row", {
      p_row_id: rowId,
      p_employee_id: employeeId ?? null,
    });
    if (error) throw new Error(error.message);
    return fromSnake<ShiftBidRow>(data) as ShiftBidRow;
  },
  releaseShiftBidRow: async (rowId: string): Promise<ShiftBidRow> => {
    const { data, error } = await supabase.rpc("release_shift_bid_row", {
      p_row_id: rowId,
    });
    if (error) throw new Error(error.message);
    return fromSnake<ShiftBidRow>(data) as ShiftBidRow;
  },
  setShiftBidSheetLocked: async (
    sheetId: string, locked: boolean,
  ): Promise<ShiftBidSheet> => {
    const { data: { session } } = await supabase.auth.getSession();
    const { data, error } = await supabase
      .from("shift_bid_sheets")
      .update({
        locked_at: locked ? new Date().toISOString() : null,
        locked_by: locked ? (session?.user?.id ?? null) : null,
      })
      .eq("id", sheetId)
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return fromSnake<ShiftBidSheet>(data) as ShiftBidSheet;
  },
  setShiftBidSheetOpen: async (
    sheetId: string, open: boolean,
  ): Promise<ShiftBidSheet> => {
    const { data, error } = await supabase
      .from("shift_bid_sheets")
      .update({
        opened_at: open ? new Date().toISOString() : null,
      })
      .eq("id", sheetId)
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return fromSnake<ShiftBidSheet>(data) as ShiftBidSheet;
  },
  // Sheets that are currently open for bidding (opened_at set, not
  // locked). Used by the layout-level "shift bid live" banner to
  // decide whether to nudge the signed-in officer. Agency-scoped
  // read via existing RLS.
  listOpenShiftBidSheets: async (): Promise<ShiftBidSheet[]> => {
    const { data, error } = await supabase
      .from("shift_bid_sheets")
      .select("*")
      .not("opened_at", "is", null)
      .is("locked_at", null);
    if (error) throw new Error(error.message);
    return fromSnakeList<ShiftBidSheet>(data);
  },
  // Rows across the given sheet ids that are already claimed by
  // the signed-in officer. Used by the banner to decide when to
  // stop nudging: if the officer's already picked a row on the
  // open sheet, banner hides. Returns fully-hydrated rows so a
  // caller can also read claim metadata (e.g. for re-showing on
  // bump).
  listMyShiftBidClaims: async (sheetIds: string[]): Promise<ShiftBidRow[]> => {
    if (sheetIds.length === 0) return [];
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return [];
    const { data: prof } = await supabase.from("profiles")
      .select("employee_id").eq("id", session.user.id).maybeSingle();
    const empId = (prof as { employee_id: string | null } | null)?.employee_id ?? null;
    if (!empId) return [];
    const { data, error } = await supabase
      .from("shift_bid_rows")
      .select("*")
      .in("sheet_id", sheetIds)
      .eq("claimed_by_employee_id", empId);
    if (error) throw new Error(error.message);
    return fromSnakeList<ShiftBidRow>(data);
  },

  // Per-user dashboard preferences: widget column layout + collapse
  // overrides. The single jsonb blob mirrors what the Dashboard
  // page keeps in local state (columns / userCollapsed /
  // userExpanded), stored server-side so a supervisor's arrangement
  // follows them across devices and reinstalls. Missing row = never
  // saved, treated as "use defaults / whatever localStorage has".
  getDashboardPrefs: async (): Promise<{
    columns: string[][];
    userCollapsed: string[];
    userExpanded: string[];
  } | null> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;
    const { data, error } = await supabase
      .from("dashboard_prefs")
      .select("data")
      .eq("user_id", session.user.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data?.data || typeof data.data !== "object") return null;
    const raw = data.data as Record<string, unknown>;
    const columns = Array.isArray(raw.columns)
      && (raw.columns as unknown[]).every((c) => Array.isArray(c))
      ? (raw.columns as string[][])
      : [];
    const userCollapsed = Array.isArray(raw.userCollapsed)
      ? (raw.userCollapsed as string[])
      : [];
    const userExpanded = Array.isArray(raw.userExpanded)
      ? (raw.userExpanded as string[])
      : [];
    return { columns, userCollapsed, userExpanded };
  },

  saveDashboardPrefs: async (input: {
    columns: string[][];
    userCollapsed: string[];
    userExpanded: string[];
  }): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    const { error } = await supabase
      .from("dashboard_prefs")
      .upsert({
        user_id: session.user.id,
        data: input,
        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id" });
    if (error) throw new Error(error.message);
  },

  // Upsert an APNs device token for the signed-in user. Called
  // from src/lib/push.ts after Apple delivers the token to the
  // Capacitor plugin. Keyed on (user_id, token) so re-registering
  // the same token from the same user is a last_seen_at bump
  // rather than a duplicate row; a new token from the same device
  // (after a reinstall) just adds a row, and APNs cleans up the
  // old one with a 410 on the next send.
  // Upsert a browser Web Push subscription for the signed-in user.
  // Called from src/lib/webpush.ts after the browser successfully
  // subscribes the service worker. Keyed on (user_id, endpoint)
  // so re-subscribing the same browser is a last_seen_at refresh.
  // RLS in 0042 limits visibility to the row's own user.
  registerWebPushSubscription: async (args: {
    endpoint: string;
    p256dh: string;
    auth: string;
    userAgent?: string;
  }): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    let agencyId: string | null = null;
    try {
      const { data: prof } = await supabase
        .from("profiles")
        .select("agency_id")
        .eq("id", session.user.id)
        .maybeSingle();
      agencyId = prof?.agency_id ?? null;
    } catch { /* best-effort */ }
    const { error } = await supabase
      .from("web_push_subscriptions")
      .upsert({
        user_id: session.user.id,
        agency_id: agencyId,
        endpoint: args.endpoint,
        p256dh: args.p256dh,
        auth: args.auth,
        user_agent: args.userAgent ?? null,
        last_seen_at: new Date().toISOString(),
        revoked_at: null,
      }, { onConflict: "user_id,endpoint" });
    if (error) {
      console.warn("registerWebPushSubscription failed:", error.message);
    }
  },

  // Drop a single subscription — called when the user opts out via
  // the Settings toggle. Hard-delete (rather than soft-revoke) so a
  // future re-opt-in starts fresh without a stale row hanging around.
  unregisterWebPushSubscription: async (endpoint: string): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    const { error } = await supabase
      .from("web_push_subscriptions")
      .delete()
      .eq("user_id", session.user.id)
      .eq("endpoint", endpoint);
    if (error) {
      console.warn("unregisterWebPushSubscription failed:", error.message);
    }
  },

  registerPushToken: async (args: {
    token: string;
    environment: "sandbox" | "production";
    bundleId: string;
  }): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    // Resolve agency_id so cross-agency audit/broadcast surfaces
    // can use it later. Soft-link via on delete set null on the FK,
    // so a missing agency just leaves the column blank.
    let agencyId: string | null = null;
    try {
      const { data: prof } = await supabase
        .from("profiles")
        .select("agency_id")
        .eq("id", session.user.id)
        .maybeSingle();
      agencyId = prof?.agency_id ?? null;
    } catch { /* best-effort */ }
    const { error } = await supabase
      .from("device_tokens")
      .upsert({
        user_id: session.user.id,
        agency_id: agencyId,
        token: args.token,
        platform: "ios",
        environment: args.environment,
        bundle_id: args.bundleId,
        last_seen_at: new Date().toISOString(),
        revoked_at: null,
      }, { onConflict: "user_id,token" });
    if (error) {
      console.warn("registerPushToken failed:", error.message);
    }
  },

  // Fan out a notification to every supervisor in the agency.
  // Optional `deptFilter`: when set, L2 supervisors are only
  // included if their linked employee's home department matches
  // that id — Owner / L1 are always included regardless. Used by
  // custom-form submissions so an L2 in Dispatch doesn't get
  // pinged for every Patrol form.
  notifySupervisors: async (
    subject: string, body: string, deptFilter?: string,
  ) => {
    try {
      const to: Record<string, unknown> = { supervisors: true };
      if (deptFilter) to.deptFilter = deptFilter;
      await supabase.functions.invoke("send-notification", {
        body: { to, subject, body },
      });
    } catch (e) {
      console.warn("notifySupervisors failed (non-fatal):", e);
    }
  },

  // Owner-triggered test notification. Unlike `notifySupervisors`,
  // this surfaces success / skipped / failure back to the caller so
  // the Settings test panel can show actual status — useful for
  // verifying the Resend integration without combing through edge
  // function logs.
  //
  // Sends to the signed-in user's own email (resolved server-side
  // via userId) so the test reaches the person who pressed the
  // button rather than fanning out to every supervisor.
  sendTestNotification: async (): Promise<{
    ok: boolean;
    detail: string;
  }> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return { ok: false, detail: "Not signed in." };
    try {
      const { data, error } = await supabase.functions.invoke(
        "send-notification",
        {
          body: {
            to: { userId: session.user.id },
            subject: "ShiftManager test notification",
            body:
              "This is a test notification triggered from Settings.\n\n"
              + "If you received this, the email pipeline is wired up "
              + "correctly. Once push notifications are added, this "
              + "same button will also send a push to your device.",
          },
        },
      );
      if (error) return { ok: false, detail: error.message };
      const payload = data as {
        sent?: number;
        failed?: number;
        rejected?: string[];
        skipped?: string;
        error?: string;
      };
      if (payload?.error) return { ok: false, detail: payload.error };
      if (payload?.skipped) {
        return {
          ok: false,
          detail: `Skipped: ${payload.skipped}`
            + (payload.rejected?.length
              ? ` · ${payload.rejected.join(", ")}` : ""),
        };
      }
      const sent = payload?.sent ?? 0;
      const failed = payload?.failed ?? 0;
      if (sent > 0 && failed === 0) {
        return { ok: true, detail: `Sent to ${sent} recipient${sent === 1 ? "" : "s"}.` };
      }
      if (sent > 0 && failed > 0) {
        return { ok: false, detail: `Partial: ${sent} sent, ${failed} failed.` };
      }
      return { ok: false, detail: "No recipients reached." };
    } catch (e) {
      return { ok: false, detail: (e as Error).message ?? "Network error." };
    }
  },

  // Materialize XXX assignments from an officer's days-off
  // pattern over the horizon (default 365 days) starting on
  // startDate. Cleans up stale auto-XXX from a previous pattern,
  // then fills matching blank dates. Manual edits
  // (from_pattern = false) are never touched, so a supervisor-
  // typed TR / V / etc. on an off-day is preserved across
  // re-applications.
  //
  // 14-day rotations: pass `week2` to alternate. Each date's
  // weekParity is `floor((date - startDate) / 7) % 2`, so days
  // 0..6 → week 1, days 7..13 → week 2, days 14..20 → week 1
  // again. Pass null/empty `week2` for a plain weekly rotation.
  applyDaysOffPattern: async (input: {
    employeeId: string;
    week1: number[];              // 0 = Sunday … 6 = Saturday
    week2?: number[] | null;      // null/undefined = single-week rotation (Week 1 every week)
    startDate: string;            // YYYY-MM-DD
    homeShiftId: string | null;   // the officer's home shift (required to materialize)
    horizonDays?: number;
    // When true, skip the "Days off updated" email. Used by the
    // Shift Bid bulk flow (which would otherwise fan out one
    // email per officer × 2 operations = 100+ messages).
    silent?: boolean;
  }): Promise<{ inserted: number; deleted: number }> => {
    const horizon = input.horizonDays ?? 365;
    const endDate = ((): string => {
      const d = new Date(input.startDate + "T00:00:00");
      d.setDate(d.getDate() + horizon);
      return d.toISOString().slice(0, 10);
    })();

    // No target shift → strip every pattern-generated XXX in the
    // window (officer no longer has a home / tac home) and return.
    // Without this, an officer moved off the schedule keeps their
    // stale XXX rows forever, polluting both the grid (orphan
    // chips on whatever shift they USED to live on) and the
    // coverage math.
    if (!input.homeShiftId) {
      const { data: stale, error: stErr } = await supabase
        .from("assignments")
        .select("id")
        .eq("employee_id", input.employeeId)
        .eq("from_pattern", true)
        .gte("date", input.startDate)
        .lte("date", endDate);
      if (stErr) throw stErr;
      const ids = (stale ?? []).map((r) => (r as { id: string }).id);
      if (ids.length > 0) {
        const { error: dErr } = await supabase
          .from("assignments").delete().in("id", ids);
        if (dErr) throw dErr;
      }
      return { inserted: 0, deleted: ids.length };
    }

    const week1Set = new Set(input.week1);
    const week2Set = input.week2 && input.week2.length > 0
      ? new Set(input.week2)
      : null;
    // For a given calendar date, which set of weekdays applies?
    // Even-week parity → week 1, odd-week parity → week 2 (or
    // week 1 if no week 2 is configured).
    const startMs = new Date(input.startDate + "T00:00:00").getTime();
    function activeSetFor(d: Date): Set<number> {
      if (!week2Set) return week1Set;
      const daysSince = Math.floor((d.getTime() - startMs) / 86_400_000);
      return Math.floor(daysSince / 7) % 2 === 0 ? week1Set : week2Set;
    }

    // Fetch only the rows we could possibly need to inspect / delete.
    const { data: existing, error: fetchErr } = await supabase
      .from("assignments")
      .select("id, date, shift_id, from_pattern, note")
      .eq("employee_id", input.employeeId)
      .gte("date", input.startDate)
      .lte("date", endDate)
      .neq("status", "cancelled");
    if (fetchErr) throw fetchErr;

    // IOD periods that overlap the window. Each materialises an
    // IOD assignment row on every covered date — they layer on
    // top of the days-off pattern (so a date that's both a
    // pattern off-day AND inside an IOD window writes IOD, not
    // XXX). A null end_date keeps filling forward through the
    // horizon.
    const { data: iodRows } = await supabase
      .from("iod_periods")
      .select("start_date, end_date")
      .eq("employee_id", input.employeeId)
      .lte("start_date", endDate)
      .or(`end_date.gte.${input.startDate},end_date.is.null`);
    const iodSet = new Set<string>();
    for (const i of iodRows ?? []) {
      const r = i as { start_date: string; end_date: string | null };
      const s = new Date(r.start_date + "T00:00:00");
      const e = r.end_date
        ? new Date(r.end_date + "T00:00:00")
        : new Date(endDate + "T00:00:00");
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        iodSet.add(d.toISOString().slice(0, 10));
      }
    }

    // Approved time-off requests on this officer that overlap the
    // window. Materialiser must skip those dates so a vacation /
    // training / leave entered by the supervisor doesn't get
    // silently stomped by an XXX from the pattern. (Manual
    // assignment edits are already protected via from_pattern =
    // false in the existing dedupe; this covers the time_off
    // table.)
    const { data: timeOffRows } = await supabase
      .from("time_off")
      .select("start_date, end_date")
      .eq("employee_id", input.employeeId)
      .eq("status", "approved")
      .lte("start_date", endDate)
      .gte("end_date", input.startDate);
    const timeOffSet = new Set<string>();
    for (const t of timeOffRows ?? []) {
      const s = new Date((t as { start_date: string }).start_date + "T00:00:00");
      const e = new Date((t as { end_date: string }).end_date + "T00:00:00");
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        timeOffSet.add(d.toISOString().slice(0, 10));
      }
    }

    // Retirement gate: the officer drops off the schedule on /
    // after this date so the materialiser must stop early. Fetched
    // from the employees row so the caller doesn't have to pass it.
    const { data: empRow } = await supabase
      .from("employees")
      .select("retirement_date")
      .eq("id", input.employeeId)
      .maybeSingle();
    const retirementDate =
      (empRow as { retirement_date: string | null } | null)?.retirement_date ?? null;

    // Stale auto rows to drop. A from_pattern row is stale when:
    //   * It's on a different shift than the new home (officer
    //     moved via a bid),
    //   * It's not a date that the pattern OR an IOD period now
    //     wants — i.e. the pattern moved Sat/Sun off → Mon/Tue
    //     off, or an IOD range was shortened, etc.
    //   * Its note doesn't match what we'd write now (a pattern
    //     XXX row on a date now inside an IOD window needs
    //     replacing with an IOD row, and vice versa).
    const wantedNoteFor = (ymd: string, wd: number): "IOD" | "XXX" | null => {
      if (iodSet.has(ymd)) return "IOD";
      const wanted = activeSetFor(new Date(ymd + "T00:00:00"));
      return wanted.has(wd) ? "XXX" : null;
    };
    const staleIds: string[] = [];
    for (const r of existing ?? []) {
      if (!r.from_pattern) continue;
      const date = new Date(r.date + "T00:00:00");
      const wd = date.getDay();
      const wantedNote = wantedNoteFor(r.date, wd);
      const currentNote = (r.note ?? "").toUpperCase();
      if (wantedNote === null
          || r.shift_id !== input.homeShiftId
          || wantedNote !== currentNote) {
        staleIds.push(r.id);
      }
    }
    if (staleIds.length > 0) {
      const { error: delErr } = await supabase
        .from("assignments").delete().in("id", staleIds);
      if (delErr) throw delErr;
    }

    // Dates we still need to insert: any pattern / IOD date in the
    // window that has no live row (after the stale deletion).
    const staleIdSet = new Set(staleIds);
    const stillExisting = new Set(
      (existing ?? [])
        .filter((r) => !staleIdSet.has(r.id))
        .map((r) => r.date),
    );

    const agencyId = await cloudAgencyId();
    const rowsToInsert: Array<Record<string, unknown>> = [];
    const cursor = new Date(input.startDate + "T00:00:00");
    const stop = new Date(endDate + "T00:00:00");
    while (cursor <= stop) {
      const wd = cursor.getDay();
      const ymd = cursor.toISOString().slice(0, 10);
      // Pick the right note for this date: IOD wins over a
      // pattern XXX (the officer is on injury leave even on what
      // would otherwise be a work day). No note ⇒ working day,
      // nothing to write.
      const wantedNote = wantedNoteFor(ymd, wd);
      if (wantedNote) {
        const beyondRetirement = retirementDate !== null && ymd >= retirementDate;
        const onApprovedTimeOff = timeOffSet.has(ymd);
        if (!stillExisting.has(ymd) && !beyondRetirement && !onApprovedTimeOff) {
          rowsToInsert.push({
            agency_id: agencyId,
            employee_id: input.employeeId,
            shift_id: input.homeShiftId,
            date: ymd,
            status: "scheduled",
            note: wantedNote,
            override_reason: null,
            coverage_group_id: null,
            ot_start_time: null,
            ot_end_time: null,
            from_pattern: true,
          });
        }
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    if (rowsToInsert.length > 0) {
      const { error: insErr } = await supabase
        .from("assignments").insert(rowsToInsert);
      if (insErr) throw insErr;
    }

    if (!input.silent) {
      const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const fmt = (xs: number[]) => xs.length > 0 ? xs.map((w) => WD[w]).join(", ") : "none";
      const names = week2Set
        ? `Week 1 ${fmt(input.week1)}; Week 2 ${fmt(input.week2 ?? [])}`
        : fmt(input.week1);
      void api.notifyEmployee(
        input.employeeId,
        "Days off updated",
        `Your regular days off have been updated to ${names} effective ${input.startDate}.\n\n`
        + "Any future days that match this pattern will be auto-marked off on the schedule. "
        + "If this is wrong, please contact your supervisor.",
      );
    }
    return { inserted: rowsToInsert.length, deleted: staleIds.length };
  },

  // Migrate an officer's existing home-shift assignments to a new
  // shift from startDate forward. Used by the Shift Bid flow when
  // the bid moves an officer to a different shift: their old-shift
  // rows (XXX / custom / etc.) after the effective date need to
  // follow them onto the new shift so future pages render them in
  // the right section and coverage math attributes them correctly.
  //
  // Scope: assignments where employee_id = E, shift_id = oldShiftId,
  // date >= startDate, status != cancelled. Cross-shift OT rows
  // (shift_id already different) are untouched; they document a
  // one-off cover on a different shift and shouldn't follow the
  // home-shift change.
  moveOfficerToShift: async (input: {
    employeeId: string;
    oldShiftId: string;
    newShiftId: string;
    startDate: string;   // YYYY-MM-DD, inclusive
    // Shift Bid bulk flow passes silent=true so a 30-officer bid
    // doesn't send one "your shift changed" email per officer
    // alongside the "days off updated" email.
    silent?: boolean;
  }): Promise<{ moved: number }> => {
    if (input.oldShiftId === input.newShiftId) return { moved: 0 };
    const { data, error } = await supabase
      .from("assignments")
      .update({ shift_id: input.newShiftId })
      .eq("employee_id", input.employeeId)
      .eq("shift_id", input.oldShiftId)
      .gte("date", input.startDate)
      .neq("status", "cancelled")
      .select("id");
    if (error) throw error;
    if (!input.silent) {
      const [oldSh, newSh] = await Promise.all([
        supabase.from("shifts").select("name").eq("id", input.oldShiftId).maybeSingle(),
        supabase.from("shifts").select("name").eq("id", input.newShiftId).maybeSingle(),
      ]);
      void api.notifyEmployee(
        input.employeeId,
        "Home shift changed",
        `Your home shift has been changed from ${oldSh.data?.name ?? "previous"}`
        + ` to ${newSh.data?.name ?? "new"} effective ${input.startDate}.\n\n`
        + "If this is wrong, please contact your supervisor.",
      );
    }
    return { moved: (data ?? []).length };
  },

  coverage: async (
    startDate: string,
    endDate: string,
    opts?: {
      departmentId?: string;
      // Optional pre-fetched inputs. When the caller already has
      // any of these in a React Query cache, passing them here
      // eliminates the equivalent Supabase round-trip inside this
      // function — an easy win on the Schedule page where every
      // one of these is already fetched by its own useQuery.
      // Anything omitted is fetched here as before.
      prefetched?: {
        shifts?: Shift[];
        employees?: Employee[];
        agency?: Agency;
        assignments?: Assignment[];
        codes?: ShiftCode[];
        timeOff?: TimeOff[];
        overrides?: CoverageOverride[];
      };
    },
  ): Promise<ShiftCoverage[]> => {
    // Fire every needed query in parallel — previously these ran
    // sequentially with ~7 awaits chained back to back, which
    // added several seconds of dead round-trip time to every
    // Schedule mount on top of the actual compute. Nothing here
    // depends on anything else, so Promise.all is safe.
    const pre = opts?.prefetched ?? {};
    const [
      shifts,
      employees,
      agency,
      assignments,
      codes,
      approvedTimeOff,
      overrideRows,
    ] = await Promise.all([
      pre.shifts ? Promise.resolve(pre.shifts) : api.listShifts({ departmentId: opts?.departmentId }),
      pre.employees ? Promise.resolve(pre.employees) : api.listEmployees(),
      pre.agency ? Promise.resolve(pre.agency) : api.getAgency(),
      pre.assignments ? Promise.resolve(pre.assignments) : api.listAssignments(startDate, endDate),
      pre.codes ? Promise.resolve(pre.codes) : api.listShiftCodes(),
      pre.timeOff ? Promise.resolve(pre.timeOff) : api.listTimeOff(startDate, endDate),
      pre.overrides ? Promise.resolve(pre.overrides) : api.listCoverageOverrides(startDate, endDate),
    ]);
    const codeMap = new Map(codes.map((c) => [c.code.toUpperCase(), c]));
    const dates = datesInRange(startDate, endDate);
    // Pre-indexes so the per-(shift, date) loops below don't scan
    // the full assignments array O(shifts × dates) times each. On a
    // typical 4-week pay period × 10 shifts × ~500 assignments this
    // trims the coverage compute from tens of thousands of scans
    // to a single build + O(1) lookups.
    const shiftById = new Map(shifts.map((s) => [s.id, s]));
    const assignmentsByDate = new Map<string, typeof assignments>();
    for (const a of assignments) {
      const bucket = assignmentsByDate.get(a.date);
      if (bucket) bucket.push(a);
      else assignmentsByDate.set(a.date, [a]);
    }

    // Approved time off acts like an implicit off-code on the home
    // cell: if an officer has no explicit assignment for that date
    // but an approved time-off request covers it, their contribution
    // drops to 0 (they're off). A manual edit wins — supervisors
    // stamping a specific code on the cell should beat the
    // approval overlay. approvedTimeOff was fetched in the parallel
    // Promise.all above; index it into a per-employee date Set.
    const timeOffDates = new Map<string, Set<string>>();
    // (employeeId|date) → Set of hour buckets the officer is off
    // for on that date because of an approved PARTIAL time-off
    // request. The bucket math below skips these hours when
    // crediting the officer's coverage, so a 13:00–16:00 partial
    // vacation on a 08:00–16:00 shift drops the officer from the
    // 13/14/15 buckets while leaving 8..12 credited. Whole-day
    // requests (start_time / end_time both null) keep going through
    // timeOffDates unchanged.
    const partialOffBuckets = new Map<string, Set<number>>();
    for (const t of approvedTimeOff) {
      if (t.status !== "approved") continue;
      if (t.startTime && t.endTime) {
        const sH = hourOf(t.startTime);
        const eH = hourOf(t.endTime);
        if (sH !== null && eH !== null) {
          const key = `${t.employeeId}|${t.startDate}`;
          const set = partialOffBuckets.get(key) ?? new Set<number>();
          for (const h of bucketsBetween(sH, eH)) set.add(h);
          partialOffBuckets.set(key, set);
        }
        // Partial rows never go into timeOffDates — they don't
        // zero out the whole-day contribution, only the covered
        // hours.
        continue;
      }
      const bucket = timeOffDates.get(t.employeeId) ?? new Set<string>();
      const start = new Date(t.startDate + "T00:00:00");
      const end = new Date(t.endDate + "T00:00:00");
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        bucket.add(d.toISOString().slice(0, 10));
      }
      timeOffDates.set(t.employeeId, bucket);
    }

    // Supervisor-stamped overrides flip a specific (shift, date)
    // back to "covered" even when the bucket math thinks it's short.
    // Built for staggered-officer shifts where the edge-hour gap
    // is de-facto absorbed by the adjacent shift overlap.
    // overrideRows was fetched in the parallel Promise.all above.
    const overrideSet = new Set(
      overrideRows.map((o) => `${o.shiftId}|${o.date}`),
    );

    // Coverage semantics:
    //   * Every active home-shift officer contributes 1.0 by default —
    //     they're scheduled for their home shift unless something says
    //     otherwise.
    //   * A known shift code (XXX, S, V, OT, OT½, …) uses its
    //     configured coverageValue.
    //   * An all-letter custom note not in the code table (e.g.
    //     "BEREAV") is treated as an off-code → 0.0.
    //   * A custom note with digits (e.g. "9-21", "14-02") is treated
    //     as a modified-hours working row → 1.0. Shifts have crossover
    //     windows with their neighbors, so an officer who leaves when
    //     the next shift starts or comes in while the previous shift
    //     is still running is still "on" for the shift — discounting
    //     to 0.5 under-counted their coverage.
    //   * Cross-shift rows use their code's coverageValue, or 1.0 when
    //     the note isn't a known code.
    //   * OT rows sharing a coverage_group_id split coverage evenly
    //     across the group (0.5 + 0.5 for a split pair; 1.0 solo).
    const empById = new Map(employees.map((e) => [e.id, e]));
    // How many rows share each coverage_group_id across the fetched
    // window. A split OT pair has size 2 (one first-half + one
    // second-half row sharing the id); a Full OT is a solo group of
    // size 1. The cross-shift loop below uses this to assign each
    // OT row a 1/size share of coverage, so a split pair sums to 1.0
    // (0.5 each) instead of 2.0 — the bug introduced when we
    // normalized the note from "OT½" (cov 0.5) to "OT" (cov 1.0).
    const groupSize = new Map<string, number>();
    for (const a of assignments) {
      if (!a.coverageGroupId) continue;
      groupSize.set(a.coverageGroupId, (groupSize.get(a.coverageGroupId) ?? 0) + 1);
    }
    const out: ShiftCoverage[] = [];
    const activeEmployees = employees.filter((e) => e.status !== "inactive");
    for (const s of shifts) {
      // Hour-bucket span of this shift. A custom hours note (e.g.
      // "12-18" on a 06-18 shift) only contributes to the hours it
      // covers, so the reported "assigned" number reads as the min
      // across buckets — surfacing the gap when one officer is out
      // for part of the shift. Falls back to a single sentinel
      // bucket for shifts without a parseable time window, which
      // collapses the math back to the old "sum of contributions"
      // behaviour so legacy data keeps working.
      const shiftStartH = hourOf(s.startTime);
      // End hour = start + duration, wrapped into 0..23. Round
      // down because partial-hour overhang at the end isn't a
      // bucket. A 12-hour 06:00 shift ends at 18:00 → bucket 18
      // is exclusive.
      const shiftEndH = shiftStartH !== null && s.durationMin > 0
        ? ((shiftStartH + Math.floor(s.durationMin / 60)) % 24)
        : null;
      // For a 24h shift, start === end so bucketsBetween returns
      // empty — fall through to the all-day enumeration instead of
      // the legacy single-bucket fallback so partial-hour notes on
      // 24h coverage still get accounted for.
      const naturalBuckets = shiftStartH !== null && shiftEndH !== null
        ? bucketsBetween(shiftStartH, shiftEndH)
        : [];
      const shiftBuckets =
        naturalBuckets.length > 0
          ? naturalBuckets
          : (shiftStartH !== null && s.durationMin >= 1440
              ? Array.from({ length: 24 }, (_, i) => (shiftStartH + i) % 24)
              : [0]); // sentinel — single-bucket shift = old summing model
      const shiftBucketSet = new Set(shiftBuckets);

      for (const date of dates) {
        // Per-date roster: officers whose effective home shift on
        // THIS date resolves to this shift. Date-aware so a tac
        // start-date in the middle of the window correctly hands
        // the same officer to their regular shift before the date
        // and their tac shift on/after.
        const roster = activeEmployees.filter(
          (e) => effectiveHomeShiftFor(agency, e, date) === s.id,
        );
        const bucketCount: Record<number, number> = {};
        const oicBucketCount: Record<number, number> = {};
        for (const h of shiftBuckets) { bucketCount[h] = 0; oicBucketCount[h] = 0; }
        // Same-day assignment slice — hoisted once per (shift, date)
        // so the roster + OT + spillover loops below don't re-scan
        // the full agency-wide assignments array O(shifts × dates)
        // times each.
        const dateAssignments = assignmentsByDate.get(date) ?? [];
        // Parallel presence sets, populated alongside the bucket
        // math and used INSTEAD of the min-across-buckets number
        // when the shift has presence_coverage = true. Counts
        // distinct officers with any contributing weight on the
        // day, ignoring the hourly bucket detail. See s.presenceCoverage
        // below for the swap. Officers on OT with rawCov <= 0 or
        // an off-code (V, S, XXX) never mark, matching the bucket
        // logic's "contributing" definition.
        const presentEmployees = new Set<string>();
        const presentOicEmployees = new Set<string>();

        // Add `weight` to every bucket in `hours` that's part of
        // this shift's window. An officer is_oic contributes to
        // the OIC counters too, so the OIC min naturally drops
        // when the OIC seat is uncovered for any hour of the day.
        //
        // Per-employee bucket dedupe: an officer can only be at one
        // place per hour, so if they're already credited to bucket H
        // via one code path (default-work, same-shift OT, cross-shift
        // OT spillover), later paths must NOT credit that same bucket
        // again. Without this a home-shift officer with an OT row
        // whose hours overlap their home shift would get counted
        // TWICE for the overlap hours — inflating shift coverage
        // (the "7/1 instead of 2/1" bug).
        const creditedBuckets = new Map<string, Set<number>>();
        const add = (emp: Employee, hours: Iterable<number>, weight: number) => {
          if (weight <= 0) return;
          // FTO officers ride along with their trainer and don't
          // count as their own seat — skip them from every coverage
          // counter. They still appear on the schedule grid via
          // their assignment row (that lives elsewhere); this only
          // affects the bucket / presence math the coverage rollup
          // reports.
          if (emp.isFto) return;
          // Partial-day time-off: any hour the officer is off for
          // is skipped as a coverage credit even though the rest of
          // their shift still contributes. `presentEmployees` still
          // picks them up as long as at least one hour lands, so a
          // presence-coverage shift with a partial-off officer
          // isn't miscounted as fully absent.
          const partialOff = partialOffBuckets.get(`${emp.id}|${date}`);
          let seen = creditedBuckets.get(emp.id);
          if (!seen) { seen = new Set<number>(); creditedBuckets.set(emp.id, seen); }
          let landed = false;
          for (const h of hours) {
            if (!shiftBucketSet.has(h)) continue;
            if (seen.has(h)) continue;
            if (partialOff?.has(h)) continue;
            seen.add(h);
            landed = true;
            bucketCount[h] = (bucketCount[h] ?? 0) + weight;
            if (emp.isOic) oicBucketCount[h] = (oicBucketCount[h] ?? 0) + weight;
          }
          if (landed) {
            presentEmployees.add(emp.id);
            if (emp.isOic) presentOicEmployees.add(emp.id);
          }
        };
        for (const emp of roster) {
          // Pick the HIGHEST-coverage row when multiple assignments
          // land on the same (officer, shift, date) — e.g. a
          // supervisor brings an off-day officer in for OT, so an
          // OT row (coverage 1.0) sits alongside the days-off
          // pattern's XXX (coverage 0). Naively picking find()'s
          // first match was order-dependent and could shadow the
          // OT behind the XXX. The "bring them in to cover" intent
          // should win.
          let a: typeof assignments[number] | undefined;
          let bestCov = -Infinity;
          for (const x of dateAssignments) {
            if (x.employeeId !== emp.id) continue;
            if (x.shiftId !== s.id) continue;
            if (x.status === "cancelled") continue;
            const xNote = (x.note ?? "").trim().toUpperCase();
            const xCov = xNote && codeMap.has(xNote)
              ? codeMap.get(xNote)!.coverageValue
              : 1;
            if (xCov > bestCov) { bestCov = xCov; a = x; }
          }
          if (!a) {
            // Default is "working home shift" — but if the officer
            // is on a Full OT covering a different shift today (solo
            // coverage_group, note OT), they're physically there,
            // not here, so they don't count for home. Split OT
            // officers aren't excluded: a split-first-half officer
            // worked the *previous* instance of home and a split-
            // second-half officer is still scheduled for their home
            // shift that day, so the default-to-working rule should
            // still apply to them.
            const onFullOtElsewhere = dateAssignments.some((x) =>
              x.employeeId === emp.id
              && x.shiftId !== s.id
              && x.status !== "cancelled"
              && !!x.coverageGroupId
              && (groupSize.get(x.coverageGroupId) ?? 0) === 1
              && (x.note ?? "").trim().toUpperCase() === "OT",
            );
            const onApprovedTimeOff = timeOffDates.get(emp.id)?.has(date) ?? false;
            // Off-code on a DIFFERENT shift: when an officer has an
            // off-code (V, S, ML, PL, XXX, …) on their raw home shift
            // but is currently rostered under their tac home (or vice
            // versa during the transition window), the per-shift
            // assignment lookup above misses the off-code and the
            // coverage default counts them as working. Physical
            // absence is real regardless of which shift section the
            // grid is rendering them under — promote any zero-
            // coverage code on a different shift into a "they're
            // off" signal on this one too.
            const offOnAnotherShift = dateAssignments.some((x) => {
              if (x.employeeId !== emp.id) return false;
              if (x.shiftId === s.id) return false;
              if (x.status === "cancelled") return false;
              const xNote = (x.note ?? "").trim().toUpperCase();
              if (!xNote) return false;
              const meta = codeMap.get(xNote);
              return !!meta && meta.coverageValue <= 0;
            });
            if (!onFullOtElsewhere && !onApprovedTimeOff && !offOnAnotherShift) {
              add(emp, shiftBuckets, 1);
            }
            continue;
          }
          const note = (a.note ?? "").trim().toUpperCase();
          if (!note) { add(emp, shiftBuckets, 1); continue; }
          if (codeMap.has(note)) {
            add(emp, shiftBuckets, codeMap.get(note)!.coverageValue);
            continue;
          }
          if (/^[A-Z]+$/.test(note)) continue; // letter-only custom = off
          // Custom hours notes (e.g. "12-18", "9-21", "14-02") —
          // try to parse and only credit the hours the officer is
          // actually present within the shift window. Unparseable
          // digit notes fall back to full-shift credit so a stray
          // "9-4" style ambiguous note doesn't silently zero out.
          const hrs = parseHourNote(note);
          if (hrs) add(emp, bucketsBetween(hrs[0], hrs[1]), 1);
          else add(emp, shiftBuckets, 1);
        }
        // Overtime coverage: every assignment on this shift/date whose
        // employee's home shift is different is a cross-shift row.
        // Zero-coverage codes (stale XXX / S / V left over from a
        // home-shift change) are ignored — they're not overtime.
        //
        // Contribution model:
        //   * OT row WITH explicit ot_start_time/ot_end_time → full
        //     rawCov (1.0 for note=OT) credited to the buckets in
        //     that window. The bucket assignment below differentiates
        //     non-overlapping halves of a split OT pair, so each
        //     officer being a SEPARATE BODY at their respective
        //     hours sums naturally. Dividing by group size here
        //     (the legacy 1/2 split share) made a fully-covered
        //     split pair read as 0.5 in every bucket and flag the
        //     "filled" shift as short on the cell that just got two
        //     officers added — exactly what the supervisor was
        //     trying to fix.
        //   * OT row WITHOUT explicit hours → legacy fallback,
        //     credit `1 / groupSize` of the shift's whole window.
        //     Preserves historical OT½ rows that pre-date the
        //     window picker.
        for (const a of dateAssignments) {
          if (a.shiftId !== s.id) continue;
          if (a.status === "cancelled") continue;
          const emp = empById.get(a.employeeId);
          if (!emp) continue;
          // Cross-shift check is date-aware too: an officer is on
          // OT here when their effective home shift FOR THIS DATE
          // doesn't match the OT shift. Without the date-aware
          // resolve, a tac-rotation officer whose tacHomeShiftId
          // matches s could be miscounted as cross-shift on tac
          // days and double-credited.
          if (effectiveHomeShiftFor(agency, emp, date) === s.id) continue;
          const note = (a.note ?? "").trim().toUpperCase();
          const rawCov = note && codeMap.has(note)
            ? codeMap.get(note)!.coverageValue
            : 1;
          if (rawCov <= 0) continue;
          // Honor ot_start_time / ot_end_time when present so a
          // split-first-half OT row only credits the early buckets
          // and the split-second-half only credits the late ones.
          // Falls back to the full shift window for OT entries that
          // don't carry explicit hours.
          let otBuckets = shiftBuckets;
          let hasExplicitHours = false;
          const otS = hourOf(a.otStartTime);
          const otE = hourOf(a.otEndTime);
          if (otS !== null && otE !== null) {
            const b = bucketsBetween(otS, otE);
            if (b.length > 0) { otBuckets = b; hasExplicitHours = true; }
          }
          const contribution = hasExplicitHours
            ? rawCov
            : a.coverageGroupId
              ? 1 / (groupSize.get(a.coverageGroupId) ?? 1)
              : rawCov;
          if (contribution <= 0) continue;
          add(emp, otBuckets, contribution);
        }

        // Cross-shift OT hour spillover. An OT row filed on shift Y
        // with explicit hours that extend PAST Y's window into this
        // shift's window (`s`) represents an officer physically at
        // work during `s`'s hours — they should count toward `s`'s
        // coverage even though the row lives on Y. Common case: a
        // Day officer picks up "06:00-18:00" on Evenings; those
        // hours cover all of Days (06-14), the first 4 hours of
        // Evenings (14-18), and none of Nights. Without this pass,
        // Days shows short because the officer's row lives on
        // Evenings and their old home-shift default-work was
        // excluded by onFullOtElsewhere.
        //
        // Scope: only spills WITHIN the same rotation family — same
        // department AND both shifts are in_rotation. Otherwise a
        // Patrol officer's Days-Evenings OT would spuriously credit
        // ACO or Dispatch just because ACO's 08-17 window happens
        // to overlap Patrol's 06-14. Patrol OT isn't the ACO
        // officer working; it's a physically distinct role.
        //
        // Only fires for rows with explicit hours (ot_start_time
        // and ot_end_time). Rows without them can't be positioned
        // in time, so we don't guess. Excludes the row's own shift
        // (covered by the loop above) and cancelled/off rows. The
        // add() helper's per-employee bucket dedupe (defined
        // above) prevents double-counting when the officer's home
        // shift also happens to be `s`.
        if (s.inRotation !== false) {
          for (const a of dateAssignments) {
            if (a.shiftId === s.id) continue;
            if (a.status === "cancelled") continue;
            const emp = empById.get(a.employeeId);
            if (!emp) continue;
            const aShift = shiftById.get(a.shiftId);
            if (!aShift) continue;
            if (aShift.inRotation === false) continue;
            if (aShift.departmentId !== s.departmentId) continue;
            const note = (a.note ?? "").trim().toUpperCase();
            const rawCov = note && codeMap.has(note)
              ? codeMap.get(note)!.coverageValue
              : 1;
            if (rawCov <= 0) continue;
            const otS = hourOf(a.otStartTime);
            const otE = hourOf(a.otEndTime);
            if (otS === null || otE === null) continue;
            const spilloverBuckets = bucketsBetween(otS, otE)
              .filter((h) => shiftBucketSet.has(h));
            if (spilloverBuckets.length === 0) continue;
            add(emp, spilloverBuckets, rawCov);
          }
        }
        // Report the WORST-COVERED bucket as the day's headline
        // number. A 06-18 shift with three full-day officers reads
        // as 3/min; a 06-18 shift with two full-day + one 12-18
        // officer reads as 2/min (the 06-12 gap), correctly
        // flagging it as short on the grid.
        //
        // Presence-mode shifts (e.g. ACO where two officers on
        // 8-4 / 9-5 alternate days off) swap that for a distinct-
        // person count on the day — either officer working alone
        // satisfies the shift, even though the union window's
        // fringe hours would otherwise show short.
        const assigned = s.presenceCoverage
          ? presentEmployees.size
          : Math.min(...shiftBuckets.map((h) => bucketCount[h] ?? 0));
        const oicAssigned = s.presenceCoverage
          ? presentOicEmployees.size
          : Math.min(...shiftBuckets.map((h) => oicBucketCount[h] ?? 0));
        out.push({
          shiftId: s.id,
          date,
          assigned,
          minStaffing: s.minStaffing,
          oicAssigned,
          // Shifts that don't enforce an OIC seat report a 0
          // requirement so every downstream "oicAssigned <
          // oicRequired" check (Schedule cell tints, Dashboard
          // shortage list, FixCoverageModal banner) naturally
          // stops flagging on this axis.
          oicRequired: s.requiresOic === false ? 0 : 1,
          overridden: overrideSet.has(`${s.id}|${date}`),
        });
      }
    }
    return out;
  },

  // ------------- Coverage overrides -------------
  // Supervisor-stamped flag that flips a specific (shift, date)
  // cell from short → covered regardless of bucket math.
  listCoverageOverrides: async (
    startDate?: string, endDate?: string,
  ): Promise<CoverageOverride[]> => {
    let q = supabase.from("coverage_overrides").select("*");
    if (startDate) q = q.gte("date", startDate);
    if (endDate)   q = q.lte("date", endDate);
    const { data, error } = await q;
    if (error) throw error;
    return fromSnakeList<CoverageOverride>(data);
  },

  setCoverageOverride: async (input: {
    shiftId: string;
    date: string;
    reason?: string | null;
  }): Promise<CoverageOverride> => {
    const agencyId = await cloudAgencyId();
    const { data: { session } } = await supabase.auth.getSession();
    // Upsert keyed by (shift_id, date) — the table's unique
    // constraint enforces one row per cell. Updating the reason
    // text just rewrites the same row.
    const { data, error } = await supabase.from("coverage_overrides")
      .upsert({
        agency_id: agencyId,
        shift_id: input.shiftId,
        date: input.date,
        reason: input.reason ?? null,
        set_by: session?.user?.id ?? null,
      }, { onConflict: "shift_id,date" })
      .select("*")
      .single();
    if (error) throw error;
    const saved = fromSnake<CoverageOverride>(data) as CoverageOverride;
    await cloudAudit("coverage_override", saved.id, "create",
      null, JSON.stringify(saved));
    return saved;
  },

  clearCoverageOverride: async (input: {
    shiftId: string;
    date: string;
  }): Promise<void> => {
    const { data: prior } = await supabase.from("coverage_overrides")
      .select("*")
      .eq("shift_id", input.shiftId)
      .eq("date", input.date)
      .maybeSingle();
    const { error } = await supabase.from("coverage_overrides").delete()
      .eq("shift_id", input.shiftId)
      .eq("date", input.date);
    if (error) throw error;
    if (prior) {
      await cloudAudit("coverage_override",
        (prior as { id: string }).id, "delete",
        JSON.stringify(prior), null);
    }
  },

  // ------------- Records overrides -------------
  // Per-(employee, date, bucket) hours overrides for the Records
  // page. See migration 0058 for the schema. `bucket` is 'work'
  // (per-day) or one of 'v','c','s','pl' (pay-period total — the
  // row's `date` is the pay-period Sunday for those).
  listRecordsOverrides: async (startDate: string, endDate: string): Promise<Array<{
    id: string;
    employeeId: string;
    date: string;
    bucket: "work" | "v" | "c" | "s" | "pl";
    hours: number;
  }>> => {
    const { data, error } = await supabase.from("records_overrides")
      .select("id, employee_id, date, bucket, hours")
      .gte("date", startDate)
      .lte("date", endDate);
    if (error) throw error;
    return (data ?? []).map((r: {
      id: string;
      employee_id: string;
      date: string;
      bucket: "work" | "v" | "c" | "s" | "pl";
      hours: number | string;
    }) => ({
      id: r.id,
      employeeId: r.employee_id,
      date: r.date,
      bucket: r.bucket,
      hours: typeof r.hours === "string" ? Number(r.hours) : r.hours,
    }));
  },
  upsertRecordsOverride: async (
    employeeId: string, date: string,
    bucket: "work" | "v" | "c" | "s" | "pl", hours: number,
  ): Promise<void> => {
    const agencyId = await cloudAgencyId();
    const { error } = await supabase.from("records_overrides")
      .upsert({
        agency_id: agencyId,
        employee_id: employeeId,
        date,
        bucket,
        hours,
      }, { onConflict: "employee_id,date,bucket" });
    if (error) throw new Error(error.message);
  },
  deleteRecordsOverride: async (
    employeeId: string, date: string,
    bucket: "work" | "v" | "c" | "s" | "pl",
  ): Promise<void> => {
    const { error } = await supabase.from("records_overrides")
      .delete()
      .eq("employee_id", employeeId)
      .eq("date", date)
      .eq("bucket", bucket);
    if (error) throw new Error(error.message);
  },

  // ------------- Schedule scenarios (mock / what-if schedules) -------------
  // Shadow tables keep every scenario fully isolated from the live
  // schedule, dashboard, coverage, records, notifications. The
  // publish RPC is the only path that moves data from the scenario
  // side into the live side.
  listScenarios: async (opts?: { departmentId?: string; includeArchived?: boolean }): Promise<ScheduleScenario[]> => {
    let q = supabase.from("schedule_scenarios")
      .select("*")
      .order("created_at", { ascending: false });
    if (opts?.departmentId) q = q.eq("department_id", opts.departmentId);
    if (!opts?.includeArchived) q = q.is("archived_at", null);
    const { data, error } = await q;
    if (error) throw error;
    return fromSnakeList<ScheduleScenario>(data);
  },
  getScenario: async (id: string): Promise<ScheduleScenario | null> => {
    const { data, error } = await supabase.from("schedule_scenarios")
      .select("*").eq("id", id).maybeSingle();
    if (error) throw error;
    return data ? (fromSnake<ScheduleScenario>(data) as ScheduleScenario) : null;
  },
  createScenario: async (input: {
    departmentId: string;
    name: string;
    startDate: string;
    endDate: string;
    seedFromLive?: boolean;
  }): Promise<ScheduleScenario> => {
    const agencyId = await cloudAgencyId();
    const { data: { session } } = await supabase.auth.getSession();
    const { data, error } = await supabase.from("schedule_scenarios")
      .insert({
        agency_id: agencyId,
        department_id: input.departmentId,
        name: input.name.trim(),
        start_date: input.startDate,
        end_date: input.endDate,
        created_by: session?.user?.id ?? null,
      })
      .select("*").single();
    if (error) throw new Error(error.message);
    const scenario = fromSnake<ScheduleScenario>(data) as ScheduleScenario;

    if (input.seedFromLive) {
      // Carry over just the days-off pattern. The user wanted a
      // clean canvas apart from each officer's XXX pattern — codes
      // (V, S, TR, custom hours, OT) stay off so what the admin
      // adds is immediately visible.
      const { data: deptEmployees } = await supabase.from("employees")
        .select("id").eq("department_id", input.departmentId)
        .is("deleted_at", null);
      const empIds = (deptEmployees ?? []).map((e: { id: string }) => e.id);
      if (empIds.length > 0) {
        const { data: liveRows } = await supabase.from("assignments")
          .select("*")
          .in("employee_id", empIds)
          .gte("date", input.startDate)
          .lte("date", input.endDate)
          .neq("status", "cancelled")
          .eq("note", "XXX");
        if (liveRows && liveRows.length > 0) {
          const toInsert = liveRows.map((r: Record<string, unknown>) => ({
            scenario_id: scenario.id,
            employee_id: r.employee_id,
            shift_id: r.shift_id,
            date: r.date,
            status: r.status,
            note: "XXX",
            override_reason: null,
            coverage_group_id: null,
            ot_start_time: null,
            ot_end_time: null,
            from_pattern: r.from_pattern,
          }));
          const { error: insErr } = await supabase.from("scenario_assignments").insert(toInsert);
          if (insErr) {
            // Non-fatal: scenario exists, seeding failed. Admin can
            // delete + retry if the seed is important.
            console.warn("seedFromLive failed:", insErr.message);
          }
        }
      }
    }
    return scenario;
  },
  updateScenario: async (
    id: string,
    patch: Partial<Pick<ScheduleScenario,
      "name" | "endDate" | "shiftOverrides" | "employeeShiftOverrides"
    >>,
  ): Promise<ScheduleScenario> => {
    const payload: Record<string, unknown> = {};
    if (patch.name !== undefined) payload.name = patch.name.trim();
    if (patch.endDate !== undefined) payload.end_date = patch.endDate;
    if (patch.shiftOverrides !== undefined) payload.shift_overrides = patch.shiftOverrides;
    if (patch.employeeShiftOverrides !== undefined) {
      payload.employee_shift_overrides = patch.employeeShiftOverrides;
    }
    const { data, error } = await supabase.from("schedule_scenarios")
      .update(payload).eq("id", id).select("*").single();
    if (error) throw new Error(error.message);
    return fromSnake<ScheduleScenario>(data) as ScheduleScenario;
  },
  deleteScenario: async (id: string): Promise<void> => {
    const { error } = await supabase.from("schedule_scenarios").delete().eq("id", id);
    if (error) throw new Error(error.message);
  },
  publishScenario: async (id: string): Promise<ScheduleScenario> => {
    const { data, error } = await supabase.rpc("publish_scenario", { p_scenario_id: id });
    if (error) throw new Error(error.message);
    return fromSnake<ScheduleScenario>(data) as ScheduleScenario;
  },

  listScenarioAssignments: async (scenarioId: string): Promise<ScenarioAssignment[]> => {
    const { data, error } = await supabase.from("scenario_assignments")
      .select("*").eq("scenario_id", scenarioId).order("date", { ascending: true });
    if (error) throw error;
    return fromSnakeList<ScenarioAssignment>(data);
  },
  upsertScenarioAssignment: async (input: {
    scenarioId: string; employeeId: string; shiftId: string; date: string;
    note?: string | null;
    otStartTime?: string | null;
    otEndTime?: string | null;
  }): Promise<ScenarioAssignment> => {
    const { data, error } = await supabase.from("scenario_assignments")
      .upsert({
        scenario_id: input.scenarioId,
        employee_id: input.employeeId,
        shift_id: input.shiftId,
        date: input.date,
        note: input.note ?? null,
        ot_start_time: input.otStartTime ?? null,
        ot_end_time: input.otEndTime ?? null,
      }, { onConflict: "scenario_id,employee_id,shift_id,date" })
      .select("*").single();
    if (error) throw new Error(error.message);
    return fromSnake<ScenarioAssignment>(data) as ScenarioAssignment;
  },
  deleteScenarioAssignment: async (id: string): Promise<void> => {
    const { error } = await supabase.from("scenario_assignments").delete().eq("id", id);
    if (error) throw new Error(error.message);
  },
  clearScenarioCell: async (input: {
    scenarioId: string; employeeId: string; date: string;
  }): Promise<void> => {
    const { error } = await supabase.from("scenario_assignments").delete()
      .eq("scenario_id", input.scenarioId)
      .eq("employee_id", input.employeeId)
      .eq("date", input.date);
    if (error) throw new Error(error.message);
  },

  listScenarioTimeOff: async (scenarioId: string): Promise<ScenarioTimeOff[]> => {
    const { data, error } = await supabase.from("scenario_time_off")
      .select("*").eq("scenario_id", scenarioId).order("start_date", { ascending: true });
    if (error) throw error;
    return fromSnakeList<ScenarioTimeOff>(data);
  },
  createScenarioTimeOff: async (input: {
    scenarioId: string; employeeId: string;
    startDate: string; endDate: string;
    startTime?: string | null; endTime?: string | null;
    type?: string; note?: string | null;
  }): Promise<ScenarioTimeOff> => {
    const { data, error } = await supabase.from("scenario_time_off").insert({
      scenario_id: input.scenarioId,
      employee_id: input.employeeId,
      start_date: input.startDate,
      end_date: input.endDate,
      start_time: input.startTime ?? null,
      end_time: input.endTime ?? null,
      type: input.type ?? "other",
      status: "approved",
      note: input.note ?? null,
    }).select("*").single();
    if (error) throw new Error(error.message);
    return fromSnake<ScenarioTimeOff>(data) as ScenarioTimeOff;
  },
  deleteScenarioTimeOff: async (id: string): Promise<void> => {
    const { error } = await supabase.from("scenario_time_off").delete().eq("id", id);
    if (error) throw new Error(error.message);
  },

  listScenarioExcluded: async (scenarioId: string): Promise<string[]> => {
    const { data, error } = await supabase.from("scenario_excluded_employees")
      .select("employee_id").eq("scenario_id", scenarioId);
    if (error) throw error;
    return (data ?? []).map((r: { employee_id: string }) => r.employee_id);
  },
  excludeScenarioEmployee: async (scenarioId: string, employeeId: string): Promise<void> => {
    const { error } = await supabase.from("scenario_excluded_employees").insert({
      scenario_id: scenarioId, employee_id: employeeId,
    });
    if (error) throw new Error(error.message);
  },
  includeScenarioEmployee: async (scenarioId: string, employeeId: string): Promise<void> => {
    const { error } = await supabase.from("scenario_excluded_employees").delete()
      .eq("scenario_id", scenarioId).eq("employee_id", employeeId);
    if (error) throw new Error(error.message);
  },

  // ------------- Time off -------------
  listTimeOff: async (startDate: string, endDate: string): Promise<TimeOff[]> => {
    // Overlap test: NOT (end_before_start OR start_after_end).
    const { data, error } = await supabase.from("time_off").select("*")
      .lte("start_date", endDate).gte("end_date", startDate)
      .order("start_date", { ascending: true });
    if (error) throw error;
    return fromSnakeList<TimeOff>(data);
  },

  requestTimeOff: async (input: {
    employeeId: string;
    startDate: string;
    endDate: string;
    type: TimeOffType;
    note: string | null;
    // Optional free-form values keyed by training_fields.key. Only
    // meaningful for training requests — ignored elsewhere.
    metadata?: Record<string, string | number | boolean | null>;
    // Optional form slug — set when the row is a custom form
    // submission (Settings → Forms) rather than a built-in time-off
    // or training request. Null / undefined for the two built-ins,
    // matching the migration default.
    formSlug?: string;
    // Optional partial-day window (HH:MM). Both null / undefined =
    // whole-day request (the historical shape). Both set = a
    // single-day partial window; the schedule cell trims the
    // officer's assignment to the un-covered portion, tints amber,
    // and the coverage math treats the missing hours as a gap.
    startTime?: string | null;
    endTime?: string | null;
  }): Promise<TimeOff> => {
    if (input.startDate > input.endDate) {
      throw new Error("End date is before start date");
    }
    // Validate partial-hours shape client-side so the DB constraint
    // error doesn't bubble up as a cryptic Postgres message.
    const st = input.startTime?.trim() || null;
    const et = input.endTime?.trim() || null;
    if ((st === null) !== (et === null)) {
      throw new Error("Set both a start time and an end time, or leave both blank for a whole-day request.");
    }
    if (st && et) {
      if (!/^\d{2}:\d{2}$/.test(st) || !/^\d{2}:\d{2}$/.test(et)) {
        throw new Error("Times must be in HH:MM format.");
      }
      if (st >= et) {
        throw new Error("End time must be after start time.");
      }
      if (input.startDate !== input.endDate) {
        throw new Error("Partial-hours requests must be a single day.");
      }
    }
    const { data, error } = await supabase.from("time_off").insert({
      employee_id: input.employeeId,
      start_date: input.startDate,
      end_date: input.endDate,
      start_time: st,
      end_time: et,
      type: input.type,
      status: "requested",
      note: input.note,
      metadata: input.metadata ?? {},
      form_slug: input.formSlug ?? null,
    }).select("*").single();
    if (error) throw error;
    const saved = fromSnake<TimeOff>(data) as TimeOff;
    await cloudAudit("time_off", saved.id, "create", null, JSON.stringify(saved));
    // Notify every Owner / L1 / L2 so they know there's a pending
    // request to review. The requester doesn't get pinged — they
    // just hit submit. Custom-form submissions dept-scope the L2
    // recipients (only L2s whose home dept matches the submitter's
    // get pinged; Owner / L1 always see all). Built-in time-off
    // and training keep the agency-wide fan-out for parity with the
    // existing supervisor-review workflow.
    void (async () => {
      const { data: emp } = await supabase
        .from("employees")
        .select("first_name, last_name, department_id")
        .eq("id", saved.employeeId)
        .maybeSingle();
      const name = emp ? `${emp.first_name} ${emp.last_name}`.trim() : "An officer";
      let formName: string | null = null;
      if (saved.formSlug) {
        const { data: form } = await supabase
          .from("forms")
          .select("name")
          .eq("slug", saved.formSlug)
          .maybeSingle();
        formName = form?.name ?? saved.formSlug;
      }
      const label = saved.formSlug
        ? `${formName ?? "form"} submission`
        : saved.type === "training"
          ? "training"
          : "time-off";
      const reviewPage = saved.formSlug
        ? `${formName ?? "form"} page`
        : saved.type === "training"
          ? "Training page"
          : "Time off page";
      const deptFilter = saved.formSlug
        ? ((emp as { department_id?: string } | null)?.department_id ?? undefined)
        : undefined;
      await api.notifySupervisors(
        `New ${label} from ${name}`,
        `${name} submitted a ${label}.\n\n`
        + (saved.formSlug
          ? ""
          : `Type: ${saved.type}\nDates: ${saved.startDate} → ${saved.endDate}\n`)
        + (saved.note ? `Note: ${saved.note}\n` : "")
        + `\nReview it in the ${reviewPage}.`,
        deptFilter,
      );
    })();
    return saved;
  },

  reviewTimeOff: async (id: string, approved: boolean): Promise<void> => {
    // Routes the status update based on (request type, caller's
    // level, current status). Two-stage rule for training:
    //
    //   training + L2  + approve         → l2_approved (partial)
    //   training + L1/Owner + approve    → approved (final)
    //                                       (works from requested
    //                                       OR l2_approved)
    //   non-training  + any L2+ + approve → approved (single-stage)
    //   deny  (any level, any type)      → denied (immediate)
    //
    // Notifications follow:
    //   l2_approved   → ping every L1/Owner ("waiting on you")
    //   approved/denied → ping the requesting officer
    const me = await cloudCurrentUser();
    if (!me) throw new Error("Not signed in");

    const { data: prior } = await supabase
      .from("time_off")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (!prior) throw new Error("Request not found");
    const priorRow = fromSnake<TimeOff>(prior) as TimeOff;

    let nextStatus: TimeOffStatus;
    if (!approved) {
      nextStatus = "denied";
    } else if (priorRow.type === "training") {
      const isFinalApprover = me.level === "owner" || me.level === "l1";
      nextStatus = isFinalApprover ? "approved" : "l2_approved";
    } else {
      nextStatus = "approved";
    }

    // Reviewer attribution. l2_approved lands in l2_approved_by/at;
    // approved/denied lands in reviewed_by/at. We never clear the
    // other slot — a training row going requested → l2_approved →
    // approved ends up with both populated, each pointing at the
    // person who took that step.
    const nowIso = new Date().toISOString();
    const patch: Record<string, unknown> = { status: nextStatus };
    if (nextStatus === "l2_approved") {
      patch.l2_approved_by = me.id;
      patch.l2_approved_at = nowIso;
    } else {
      patch.reviewed_by = me.id;
      patch.reviewed_at = nowIso;
    }

    const { error } = await supabase.from("time_off")
      .update(patch).eq("id", id);
    if (error) throw error;
    await cloudAudit("time_off", id, "update",
      null, JSON.stringify({ status: nextStatus }));

    // Comp-hours accrual. If the submission is a custom form and
    // final-approved, sum every `comp_hours` field value from the
    // row's metadata and add it to the officer's records_overrides
    // 'c' bucket for the pay-period Sunday containing start_date.
    // Multiple comp_hours fields on one form stack. Deny / partial
    // approval don't touch the bank. Best-effort — a failed bump
    // doesn't roll back the status update.
    if (nextStatus === "approved" && priorRow.formSlug) {
      try {
        const agencyIdForBump = await cloudAgencyId();
        const { data: fieldRows } = await supabase.from("training_fields")
          .select("key, field_type")
          .eq("scope", priorRow.formSlug as string)
          .eq("field_type", "comp_hours");
        const compKeys = (fieldRows ?? [])
          .map((r: { key: string }) => r.key);
        let total = 0;
        for (const k of compKeys) {
          const v = priorRow.metadata?.[k];
          const n = typeof v === "string" ? Number(v) : (typeof v === "number" ? v : NaN);
          if (Number.isFinite(n) && n > 0) total += n;
        }
        if (total > 0) {
          // Pay-period Sunday containing the submission's start_date
          // — matches the Records page's biweekly period keying.
          const d = new Date(priorRow.startDate + "T00:00:00");
          d.setHours(0, 0, 0, 0);
          d.setDate(d.getDate() - d.getDay());
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          const periodStart = `${y}-${m}-${dd}`;
          const { data: existing } = await supabase.from("records_overrides")
            .select("hours")
            .eq("employee_id", priorRow.employeeId)
            .eq("date", periodStart)
            .eq("bucket", "c")
            .maybeSingle();
          const prev = existing
            ? (typeof existing.hours === "string" ? Number(existing.hours) : existing.hours as number)
            : 0;
          const next = (prev || 0) + total;
          await supabase.from("records_overrides").upsert({
            agency_id: agencyIdForBump,
            employee_id: priorRow.employeeId,
            date: periodStart,
            bucket: "c",
            hours: next,
          }, { onConflict: "employee_id,date,bucket" });
        }
      } catch (e) {
        console.warn("comp_hours accrual failed (non-fatal):", e);
      }
    }

    // Notify the relevant audience.
    const isTraining = priorRow.type === "training";
    const isForm = !!priorRow.formSlug;
    let formName: string | null = null;
    if (isForm) {
      const { data: form } = await supabase
        .from("forms")
        .select("name")
        .eq("slug", priorRow.formSlug as string)
        .maybeSingle();
      formName = form?.name ?? priorRow.formSlug;
    }
    const label = isForm
      ? `${formName ?? "Form"} submission`
      : isTraining
        ? "Training request"
        : `${priorRow.type} time-off request`;
    if (nextStatus === "l2_approved") {
      // Partial approval — bubble up to the agency's final
      // approvers so they know there's something waiting on them.
      void api.notifySupervisors(
        `${label} awaiting final approval`,
        `An L2 supervisor approved a ${label.toLowerCase()}. It needs an Owner or Level 1 review to finalise.\n\n`
        + `Dates: ${priorRow.startDate} → ${priorRow.endDate}\n`
        + (priorRow.note ? `Note: ${priorRow.note}\n` : "")
        + `\nReview it on the Training page.`,
      );
    } else {
      void api.notifyEmployee(
        priorRow.employeeId,
        `${label} ${nextStatus}`,
        isForm
          ? `Your ${label.toLowerCase()} has been ${nextStatus}.`
          : `Your ${label.toLowerCase()} for ${priorRow.startDate} through ${priorRow.endDate} has been ${nextStatus}.`,
      );
    }
  },

  // Drop a single day out of every approved time-off range that
  // currently covers (employeeId, date). Used by the schedule grid
  // so supervisors can clear or overwrite an approved-time-off chip
  // without having to revoke the whole request in the Time-off page.
  //
  // A range that begins or ends on the target date gets trimmed by
  // one day; a single-day range is deleted; an interior hit gets
  // split into two rows ([start..date-1] and [date+1..end]) so the
  // remaining days keep their approval.
  removeTimeOffDate: async (
    employeeId: string, date: string,
  ): Promise<void> => {
    const { data, error } = await supabase.from("time_off")
      .select("*")
      .eq("employee_id", employeeId)
      .eq("status", "approved")
      .lte("start_date", date)
      .gte("end_date", date);
    if (error) throw error;
    const rows = fromSnakeList<TimeOff>(data);
    const dayBefore = ymd(addDays(new Date(date + "T00:00:00"), -1));
    const dayAfter = ymd(addDays(new Date(date + "T00:00:00"), 1));
    for (const r of rows) {
      if (r.startDate === date && r.endDate === date) {
        const { error: delErr } = await supabase.from("time_off")
          .delete().eq("id", r.id);
        if (delErr) throw delErr;
        await cloudAudit("time_off", r.id, "delete",
          JSON.stringify(r), null);
      } else if (r.startDate === date) {
        const { error: upErr } = await supabase.from("time_off")
          .update({ start_date: dayAfter }).eq("id", r.id);
        if (upErr) throw upErr;
        await cloudAudit("time_off", r.id, "update",
          null, JSON.stringify({ start_date: dayAfter }));
      } else if (r.endDate === date) {
        const { error: upErr } = await supabase.from("time_off")
          .update({ end_date: dayBefore }).eq("id", r.id);
        if (upErr) throw upErr;
        await cloudAudit("time_off", r.id, "update",
          null, JSON.stringify({ end_date: dayBefore }));
      } else {
        // Interior split: shrink the original, insert a new row
        // for the trailing segment.
        const origEnd = r.endDate;
        const { error: upErr } = await supabase.from("time_off")
          .update({ end_date: dayBefore }).eq("id", r.id);
        if (upErr) throw upErr;
        const { data: inserted, error: insErr } = await supabase
          .from("time_off").insert({
            employee_id: r.employeeId,
            start_date: dayAfter,
            end_date: origEnd,
            type: r.type,
            status: "approved",
            note: r.note,
          }).select("*").single();
        if (insErr) throw insErr;
        await cloudAudit("time_off", r.id, "update",
          null, JSON.stringify({ end_date: dayBefore }));
        const newRow = fromSnake<TimeOff>(inserted) as TimeOff;
        await cloudAudit("time_off", newRow.id, "create",
          null, JSON.stringify(newRow));
      }
    }
  },

  // ------------- Request field catalogue -------------
  //
  // Owner / L1 own these lists. Each scope (training, time_off) has
  // its own ordered list of fields; request forms render one input
  // per row (in sort_order) and values land in time_off.metadata
  // keyed by `key`.
  // -------- Forms (Settings → Forms) --------
  //
  // Returns every form for the current agency — the two built-ins
  // (Time off, Training) synthesised on the fly plus any custom
  // forms an admin has created from Settings → Forms. Built-ins
  // aren't rows in the `forms` table so they can never be
  // accidentally deleted; the API just tags them `builtin: true`.
  listForms: async (): Promise<Form[]> => {
    const agencyId = await cloudAgencyId();
    const { data, error } = await supabase.from("forms")
      .select("*").order("name", { ascending: true });
    if (error) throw error;
    const custom = fromSnakeList<Form>(data).map((f) => ({
      ...f,
      builtin: false,
    }));
    const builtins: Form[] = [
      {
        id: "", agencyId, slug: "time_off", name: "Time off",
        builtin: true, createdAt: null,
      },
      {
        id: "", agencyId, slug: "training", name: "Training",
        builtin: true, createdAt: null,
      },
    ];
    return [...builtins, ...custom];
  },

  createForm: async (input: {
    slug: string;
    name: string;
  }): Promise<Form> => {
    const agencyId = await cloudAgencyId();
    const slug = input.slug.trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_")
      .replace(/^_+|_+$/g, "");
    if (!slug) throw new Error("Slug is required.");
    if (slug === "time_off" || slug === "training") {
      throw new Error(`"${slug}" is reserved for the built-in form.`);
    }
    const name = input.name.trim();
    if (!name) throw new Error("Name is required.");
    const { data, error } = await supabase.from("forms").insert({
      agency_id: agencyId,
      slug,
      name,
    }).select("*").single();
    if (error) {
      // 23505 = unique constraint. Surface a helpful message
      // instead of the raw Postgres text.
      if ((error as { code?: string }).code === "23505") {
        throw new Error(`A form with the slug "${slug}" already exists.`);
      }
      throw error;
    }
    const row = { ...(fromSnake<Form>(data) as Form), builtin: false };
    await cloudAudit("form", row.id, "create", null, JSON.stringify(row));
    return row;
  },

  // Coverage requests: an employee-initiated ask to cover a
  // shortage on a specific (shift, date). Rows carry the requested
  // coverage type (full / split_first / split_second) and a
  // short note; supervisors review with an approve/deny + comment.
  // On approve, a follow-up updateCoverageRequestDecision call
  // records the created OT assignment id back on the row.
  listCoverageRequests: async (input?: {
    status?: CoverageRequestStatus;
    onlyMine?: boolean;
  }): Promise<CoverageRequest[]> => {
    let q = supabase.from("coverage_requests")
      .select("*")
      .order("created_at", { ascending: false });
    if (input?.status) q = q.eq("status", input.status);
    if (input?.onlyMine) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return [];
      q = q.eq("requester_user_id", session.user.id);
    }
    const { data, error } = await q;
    if (error) throw error;
    return fromSnakeList<CoverageRequest>(data);
  },

  createCoverageRequest: async (input: {
    shiftId: string;
    departmentId: string;
    date: string;
    coverageType: CoverageRequestType;
    requesterNote?: string;
  }): Promise<CoverageRequest> => {
    const agencyId = await cloudAgencyId();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error("Not signed in.");
    // Resolve the requester's employee id via their profile.
    const { data: prof } = await supabase.from("profiles")
      .select("employee_id").eq("id", session.user.id).maybeSingle();
    const employeeId = (prof as { employee_id: string | null } | null)
      ?.employee_id ?? null;
    if (!employeeId) {
      throw new Error(
        "Your account isn't linked to an employee record — ask a supervisor to link it before submitting.",
      );
    }
    // Reject duplicates: one pending request per (requester, shift,
    // date). The unique concept isn't enforced in SQL because the
    // cancel/approve flow generates history rows; we just check
    // client-side before inserting.
    const { data: existing } = await supabase.from("coverage_requests")
      .select("id")
      .eq("requester_employee_id", employeeId)
      .eq("shift_id", input.shiftId)
      .eq("date", input.date)
      .eq("status", "pending")
      .maybeSingle();
    if (existing) {
      throw new Error("You already have a pending request for this shift.");
    }
    const row = {
      agency_id: agencyId,
      shift_id: input.shiftId,
      department_id: input.departmentId,
      date: input.date,
      requester_employee_id: employeeId,
      requester_user_id: session.user.id,
      coverage_type: input.coverageType,
      requester_note: input.requesterNote?.trim() || null,
      status: "pending",
    };
    const { data, error } = await supabase.from("coverage_requests")
      .insert(row).select("*").single();
    if (error) throw error;
    const saved = fromSnake<CoverageRequest>(data) as CoverageRequest;
    await cloudAudit("coverage_request", saved.id, "create",
      null, JSON.stringify(saved));
    // Fan out to supervisors in the shift's department. L2s outside
    // the dept are filtered out by the edge function; Owner / L1
    // always get pinged.
    await api.notifySupervisors(
      "Coverage request",
      `An officer requested to cover a short shift on ${input.date}.\nOpen the dashboard to review and approve or deny.`,
      input.departmentId,
    );
    return saved;
  },

  // Requester cancels their own pending request. RLS "cancel own
  // pending" gate scopes this to (requester, pending) rows only.
  cancelCoverageRequest: async (id: string): Promise<void> => {
    const { error } = await supabase.from("coverage_requests")
      .update({ status: "cancelled", decided_at: new Date().toISOString() })
      .eq("id", id);
    if (error) throw error;
    await cloudAudit("coverage_request", id, "update",
      null, JSON.stringify({ status: "cancelled" }));
  },

  // Supervisor approve / deny path. On approve the caller passes
  // the created OT assignment id (built by the review modal from
  // the same OT-shape createAssignment path Fix-coverage uses)
  // so it can be linked back on the row.
  decideCoverageRequest: async (input: {
    id: string;
    decision: "approved" | "denied";
    comment?: string;
    assignmentId?: string | null;
  }): Promise<CoverageRequest> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error("Not signed in.");
    const patch: Record<string, unknown> = {
      status: input.decision,
      reviewer_user_id: session.user.id,
      reviewer_comment: input.comment?.trim() || null,
      decided_at: new Date().toISOString(),
    };
    if (input.decision === "approved" && input.assignmentId) {
      patch.assignment_id = input.assignmentId;
    }
    const { data, error } = await supabase.from("coverage_requests")
      .update(patch).eq("id", input.id).select("*").single();
    if (error) throw error;
    const saved = fromSnake<CoverageRequest>(data) as CoverageRequest;
    await cloudAudit("coverage_request", saved.id, "update",
      null, JSON.stringify(saved));
    // Notify the requester of the decision. Silent-drop when the
    // request row somehow lost its user_id (deleted account, etc.).
    if (saved.requesterUserId) {
      const verb = input.decision === "approved" ? "approved" : "denied";
      const commentLine = input.comment?.trim()
        ? `\n\nComment: ${input.comment.trim()}`
        : "";
      const scheduleLine = input.decision === "approved"
        ? "\n\nYou've been added to the schedule for this shift."
        : "";
      await api.notifyUser(
        saved.requesterUserId,
        `Coverage request ${verb}`,
        `Your coverage request for ${saved.date} was ${verb}.${scheduleLine}${commentLine}`,
      );
    }
    return saved;
  },

  // Hard-deletes the form and cascades to every request_field with a
  // matching scope, so orphaned field definitions don't linger.
  // Existing time_off.metadata values that referenced those field
  // keys stay on the request rows (data is preserved; the labels
  // just stop resolving from a live definition).
  deleteForm: async (id: string): Promise<void> => {
    const { data: prior } = await supabase.from("forms")
      .select("*").eq("id", id).maybeSingle();
    if (!prior) throw new Error("Form not found.");
    // Cascade the field-catalogue delete first so we don't leave
    // orphaned rows if the form delete fails partway.
    const slug = (prior as { slug: string }).slug;
    const { error: e1 } = await supabase.from("training_fields")
      .delete().eq("scope", slug);
    if (e1) throw e1;
    const { error: e2 } = await supabase.from("forms").delete().eq("id", id);
    if (e2) throw e2;
    await cloudAudit("form", id, "delete",
      JSON.stringify(prior), null);
  },

  listRequestFields: async (scope: RequestFieldScope): Promise<RequestField[]> => {
    const { data, error } = await supabase.from("training_fields")
      .select("*").eq("scope", scope)
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return fromSnakeList<RequestField>(data);
  },

  createRequestField: async (input: {
    scope: RequestFieldScope;
    key: string;
    label: string;
    fieldType: RequestFieldType;
    sortOrder?: number;
    required?: boolean;
    options?: string[];
  }): Promise<RequestField> => {
    const agencyId = await cloudAgencyId();
    const key = input.key.trim();
    if (!key) throw new Error("Field key is required");
    if (!input.label.trim()) throw new Error("Field label is required");
    const { data, error } = await supabase.from("training_fields").insert({
      agency_id: agencyId,
      scope: input.scope,
      key,
      label: input.label.trim(),
      field_type: input.fieldType,
      sort_order: input.sortOrder ?? 0,
      required: input.required ?? false,
      // Only persist non-empty option lists for dropdown fields;
      // every other type keeps the default [] on the row.
      options: input.fieldType === "dropdown"
        ? (input.options ?? []).map((o) => o.trim()).filter(Boolean)
        : [],
    }).select("*").single();
    if (error) throw error;
    const row = fromSnake<RequestField>(data) as RequestField;
    await cloudAudit("request_field", row.id, "create",
      null, JSON.stringify(row));
    return row;
  },

  updateRequestField: async (
    id: string,
    patch: Partial<Pick<RequestField,
      "key" | "label" | "fieldType" | "sortOrder" | "required" | "options">>,
  ): Promise<RequestField> => {
    const payload: Record<string, unknown> = {};
    if (patch.key !== undefined) payload.key = patch.key.trim();
    if (patch.label !== undefined) payload.label = patch.label.trim();
    if (patch.fieldType !== undefined) payload.field_type = patch.fieldType;
    if (patch.sortOrder !== undefined) payload.sort_order = patch.sortOrder;
    if (patch.required !== undefined) payload.required = patch.required;
    if (patch.options !== undefined) {
      payload.options = (patch.options ?? []).map((o) => o.trim()).filter(Boolean);
    }
    const { data, error } = await supabase.from("training_fields")
      .update(payload).eq("id", id).select("*").single();
    if (error) throw error;
    const row = fromSnake<RequestField>(data) as RequestField;
    await cloudAudit("request_field", id, "update",
      null, JSON.stringify(payload));
    return row;
  },

  deleteRequestField: async (id: string): Promise<void> => {
    const { data: prior } = await supabase.from("training_fields")
      .select("*").eq("id", id).maybeSingle();
    const { error } = await supabase.from("training_fields")
      .delete().eq("id", id);
    if (error) throw error;
    await cloudAudit("request_field", id, "delete",
      prior ? JSON.stringify(prior) : null, null);
  },

  // ------------- Settings (Supabase) -------------
  getAgency: async (): Promise<Agency> => {
    const { data, error } = await supabase.from("agencies")
      .select("*").limit(1).single();
    if (error) throw error;
    return fromSnake<Agency>(data) as Agency;
  },

  updateAgency: async (
    patch: Pick<Agency, "name" | "timezone" | "weekStart" | "minStaffing">,
  ): Promise<Agency> => {
    const a = await api.getAgency();
    const { data, error } = await supabase.from("agencies")
      .update({
        name: patch.name,
        timezone: patch.timezone,
        week_start: patch.weekStart,
        min_staffing: patch.minStaffing,
      })
      .eq("id", a.id).select("*").single();
    if (error) throw error;
    const updated = fromSnake<Agency>(data) as Agency;
    await cloudAudit("agency", a.id, "update", JSON.stringify(a), JSON.stringify(updated));
    return updated;
  },

  // Agency-wide schedule anchor. Persisted on the agency row so
  // every user in every browser lands on the same pay period. Null
  // clears the anchor (falls back to "today's pay period"). Caller
  // is responsible for the permission check — only Owner / L1
  // should reach this from the UI.
  setScheduleAnchor: async (date: string | null): Promise<Agency> => {
    const a = await api.getAgency();
    const { data, error } = await supabase.from("agencies")
      .update({ schedule_anchor_date: date })
      .eq("id", a.id).select("*").single();
    if (error) throw error;
    return fromSnake<Agency>(data) as Agency;
  },

  // Agency-wide "which optional nav features are enabled" list.
  // Anything in the array is hidden from the sidebar / iOS menu
  // (see disabledFeatures on the Agency type). Empty list = every
  // feature enabled (the default for every existing agency). L1+
  // gated in the UI; the DB has no policy since the write already
  // requires an authenticated user with the agency row.
  setDisabledFeatures: async (features: string[]): Promise<Agency> => {
    const a = await api.getAgency();
    const clean = Array.from(new Set(features.map((f) => f.trim()).filter(Boolean)));
    const { data, error } = await supabase.from("agencies")
      .update({ disabled_features: clean })
      .eq("id", a.id).select("*").single();
    if (error) throw error;
    const updated = fromSnake<Agency>(data) as Agency;
    await cloudAudit(
      "agency", a.id, "update",
      JSON.stringify({ disabledFeatures: a.disabledFeatures ?? [] }),
      JSON.stringify({ disabledFeatures: updated.disabledFeatures ?? [] }),
    );
    return updated;
  },

  // Agency-wide tac mode toggle. Owner/L1 only — caller gates.
  // Audit-logged so the audit trail shows when the agency entered
  // and exited the tac state.
  //
  // After flipping the flag we re-materialise each followsTacMode
  // officer's auto-XXX pattern using the *currently-active* pattern
  // (tac when activating, regular when deactivating). applyDaysOff-
  // Pattern only touches `from_pattern` rows, so any supervisor-
  // typed cells (TR, S, manual XXX, OT, …) survive untouched. The
  // schedule grid filtering is still handled at render time by
  // applyTacOverlay — this step just makes sure the XXX cells line
  // up with the new weekday pattern.
  setTacModeActive: async (
    active: boolean,
    startDate: string | null = null,
  ): Promise<Agency> => {
    const a = await api.getAgency();
    // Turning the master flag off ALWAYS clears the start-date
    // anchor so the next on-flip starts fresh — otherwise a stale
    // future date could re-arm the transition the moment the flag
    // came back. When activating, the caller's startDate value
    // (possibly null = "tac throughout, no transition") is taken
    // verbatim.
    const patch = active
      ? { tac_mode_active: true, tac_mode_start_date: startDate }
      : { tac_mode_active: false, tac_mode_start_date: null };
    const { data, error } = await supabase.from("agencies")
      .update(patch)
      .eq("id", a.id).select("*").single();
    if (error) throw error;
    const updated = fromSnake<Agency>(data) as Agency;
    await cloudAudit("agency", a.id, "update",
      JSON.stringify({
        tacModeActive: a.tacModeActive,
        tacModeStartDate: a.tacModeStartDate,
      }),
      JSON.stringify({
        tacModeActive: updated.tacModeActive,
        tacModeStartDate: updated.tacModeStartDate,
      }));

    const employees = await api.listEmployees();
    const today = new Date().toISOString().slice(0, 10);
    for (const emp of employees) {
      if (!emp.followsTacMode) continue;
      const week1 = active ? emp.tacDaysOffWeekdays : emp.daysOffWeekdays;
      const week2 = active ? emp.tacDaysOffWeekdaysW2 : emp.daysOffWeekdaysW2;
      const homeShiftId = active ? emp.tacHomeShiftId : emp.homeShiftId;
      if (!homeShiftId) continue;
      await api.applyDaysOffPattern({
        employeeId: emp.id,
        week1: week1 ?? [],
        week2: week2 ?? null,
        startDate: emp.daysOffStartDate ?? today,
        homeShiftId,
        silent: true,
      });
    }
    return updated;
  },

  // ------------- Branding (Supabase, via settings table) -------------
  getBrandImage: async (): Promise<string | null> => {
    const agencyId = await cloudAgencyId();
    const { data } = await supabase.from("settings")
      .select("value").eq("agency_id", agencyId).eq("key", "brand_image").maybeSingle();
    const value = (data?.value as string | undefined) ?? null;
    // Cache to localStorage so the next-cold-start branded loading
    // splash (src/App.tsx) can paint the agency patch immediately
    // without waiting for this network call. Best-effort; iOS WKWebView
    // localStorage occasionally throws under quota pressure.
    try {
      if (value) localStorage.setItem("shift_manager.brand_image", value);
      else localStorage.removeItem("shift_manager.brand_image");
    } catch { /* noop */ }
    return value;
  },
  setBrandImage: async (dataUrl: string | null): Promise<void> => {
    const agencyId = await cloudAgencyId();
    if (dataUrl) {
      await supabase.from("settings").upsert(
        { agency_id: agencyId, key: "brand_image", value: dataUrl },
        { onConflict: "agency_id,key" },
      );
    } else {
      await supabase.from("settings").delete()
        .eq("agency_id", agencyId).eq("key", "brand_image");
    }
    // brand_image holds a base64 data URL; logging "set" / "cleared"
    // keeps the audit row small instead of storing the whole image.
    await cloudAudit("settings", "brand_image", "update",
      null, JSON.stringify({ brandImage: dataUrl ? "set" : "cleared" }));
  },

  // ------------- Seniority mode (Supabase, via settings table) -------------
  // Drives how the schedule grid sorts officers inside a shift
  // section. "rank_badge" matches the historical behaviour (rank
  // tier → badge number). "radio_rank_badge" promotes the radio
  // call sign to the lead key when present, falling back to
  // rank+badge for officers without one.
  getSeniorityMode: async (): Promise<"rank_badge" | "radio_rank_badge"> => {
    const agencyId = await cloudAgencyId();
    const { data } = await supabase.from("settings")
      .select("value").eq("agency_id", agencyId).eq("key", "seniority_mode").maybeSingle();
    return (data?.value as "rank_badge" | "radio_rank_badge" | undefined) ?? "rank_badge";
  },
  setSeniorityMode: async (mode: "rank_badge" | "radio_rank_badge"): Promise<void> => {
    const agencyId = await cloudAgencyId();
    await supabase.from("settings").upsert(
      { agency_id: agencyId, key: "seniority_mode", value: mode },
      { onConflict: "agency_id,key" },
    );
    await cloudAudit("settings", "seniority_mode", "update",
      null, JSON.stringify({ seniorityMode: mode }));
  },

  // ------------- Backups -------------
  // Manual snapshots + hourly cron entries live in the same table.
  // List returns metadata only (no jsonb blob) so the Settings
  // panel stays snappy.
  listBackups: async (): Promise<Backup[]> => {
    const { data, error } = await supabase.from("backups")
      .select("id, agency_id, created_at, created_by, kind, label, size_bytes")
      .order("created_at", { ascending: false })
      .limit(8);
    if (error) throw error;
    return fromSnakeList<Backup>(data);
  },

  // Trigger snapshot_agency() server-side. Retention (last 8 per
  // agency) is enforced inside the function so we don't have to
  // race a trim from the client.
  createBackup: async (label?: string): Promise<string> => {
    const agencyId = await cloudAgencyId();
    const { data: { session } } = await supabase.auth.getSession();
    const { data, error } = await supabase.rpc("snapshot_agency", {
      p_agency_id: agencyId,
      p_kind: "manual",
      p_label: label ?? null,
      p_user: session?.user?.id ?? null,
    });
    if (error) throw error;
    await cloudAudit("backup", data as string, "create",
      null, JSON.stringify({ kind: "manual", label: label ?? null }));
    return data as string;
  },

  // restore_backup() audits its own row server-side and wipes
  // current data before replaying. Caller permission (owner / l1)
  // is enforced inside the function — if a lower-level user calls
  // this via the API, the function raises.
  restoreBackup: async (backupId: string): Promise<void> => {
    const { error } = await supabase.rpc("restore_backup", { p_backup_id: backupId });
    if (error) throw error;
  },

  deleteBackup: async (backupId: string): Promise<void> => {
    const { data: prior } = await supabase.from("backups")
      .select("id, kind, label, created_at").eq("id", backupId).maybeSingle();
    const { error } = await supabase.from("backups").delete().eq("id", backupId);
    if (error) throw error;
    await cloudAudit("backup", backupId, "delete",
      prior ? JSON.stringify(prior) : null, null);
  },

  // Pull the raw snapshot JSON for download. Owner / L1 only via
  // RLS on the backups table.
  downloadBackup: async (backupId: string): Promise<unknown> => {
    const { data, error } = await supabase.from("backups")
      .select("snapshot").eq("id", backupId).single();
    if (error) throw error;
    return (data as { snapshot: unknown }).snapshot;
  },

  // ------------- Display prefs (Supabase, via settings table) -------------
  getPayWeekendWeeks: async (): Promise<number[]> => {
    const agencyId = await cloudAgencyId();
    const { data } = await supabase.from("settings")
      .select("value").eq("agency_id", agencyId).eq("key", "pay_weekend_weeks").maybeSingle();
    const raw = data?.value as string | undefined;
    if (!raw) return [0, 2];
    return raw.split(",").map((s) => Number(s.trim()))
      .filter((n) => Number.isInteger(n) && n >= 0 && n <= 3);
  },
  setPayWeekendWeeks: async (weeks: number[]): Promise<void> => {
    const agencyId = await cloudAgencyId();
    const uniq = Array.from(new Set(weeks.filter((n) => n >= 0 && n <= 3))).sort();
    await supabase.from("settings").upsert(
      { agency_id: agencyId, key: "pay_weekend_weeks", value: uniq.join(",") },
      { onConflict: "agency_id,key" },
    );
    await cloudAudit("settings", "pay_weekend_weeks", "update",
      null, JSON.stringify({ payWeekendWeeks: uniq }));
  },

  // ------------- Audit (Supabase) -------------
  // Options:
  //   limit  — page size (server-side cap, default 500)
  //   before — cursor: only rows strictly older than this ISO
  //            timestamp. Used by "Load older" pagination.
  //   from   — inclusive lower bound on created_at (ISO / YYYY-MM-DD)
  //   to     — inclusive upper bound on created_at (ISO / YYYY-MM-DD)
  //   cell   — restrict to assignment audit rows whose before/after
  //            snapshot references this exact (employee, date, shift)
  //            tuple. Powers the Schedule cell-history link.
  // The `from` / `to` window is what the Audit page's date filter
  // uses to jump beyond the initial 500-row window without paging.
  listAudit: async (
    opts: {
      limit?: number;
      before?: string;
      from?: string;
      to?: string;
      cell?: { employeeId: string; date: string; shiftId: string };
    } = {},
  ): Promise<AuditEntry[]> => {
    const limit = opts.limit ?? 500;
    let q = supabase.from("audit_log")
      .select("*").order("created_at", { ascending: false }).limit(limit);
    if (opts.before) q = q.lt("created_at", opts.before);
    if (opts.from) q = q.gte("created_at", opts.from);
    if (opts.to) {
      // Bare YYYY-MM-DD from a <input type="date"> should include
      // the whole calendar day, so bump to the *next* day's 00:00.
      const to = /^\d{4}-\d{2}-\d{2}$/.test(opts.to)
        ? new Date(new Date(opts.to + "T00:00:00").getTime() + 24 * 60 * 60 * 1000).toISOString()
        : opts.to;
      q = q.lt("created_at", to);
    }
    if (opts.cell) {
      // The tuple lives inside either before_json (delete) or
      // after_json (create / update). Match if EITHER snapshot has
      // all three keys set to the target values. PostgREST's `or`
      // takes a comma-separated list of expressions; `and(...)`
      // groups the three column matches per side.
      //
      // The writers historically stamped these snapshots with two
      // different key styles — createAssignment JSON-stringified a
      // camelCase Assignment object (employeeId / shiftId) while
      // deleteAssignment / clearCellAssignments stringified the raw
      // Supabase row (employee_id / shift_id). Old rows on record
      // still use both. Match either style so history for any given
      // cell surfaces every event that ever touched it.
      const { employeeId, date, shiftId } = opts.cell;
      const eq = (col: string, val: string) =>
        `${col}.eq.${encodeURIComponent(val)}`;
      const groups: string[] = [];
      for (const side of ["after_json", "before_json"] as const) {
        // snake_case (raw Supabase row snapshot)
        groups.push(`and(${eq(`${side}->>employee_id`, employeeId)},${eq(`${side}->>shift_id`, shiftId)},${eq(`${side}->>date`, date)})`);
        // camelCase (Assignment object snapshot)
        groups.push(`and(${eq(`${side}->>employeeId`, employeeId)},${eq(`${side}->>shiftId`, shiftId)},${eq(`${side}->>date`, date)})`);
      }
      q = q.eq("entity_type", "assignment").or(groups.join(","));
    }
    const { data, error } = await q;
    if (error) throw error;
    return fromSnakeList<AuditEntry>(data);
  },

  // ------------- Shift codes (Supabase) -------------
  listShiftCodes: async (): Promise<ShiftCode[]> => {
    const { data, error } = await supabase.from("shift_codes")
      .select("*").order("sort_order", { ascending: true });
    if (error) throw error;
    return fromSnakeList<ShiftCode>(data);
  },

  createShiftCode: async (input: Partial<ShiftCode>): Promise<ShiftCode> => {
    if (!input.code?.trim()) throw new Error("Code required");
    const agencyId = await cloudAgencyId();
    const { count } = await supabase.from("shift_codes")
      .select("*", { count: "exact", head: true });
    const { data, error } = await supabase.from("shift_codes")
      .insert({
        agency_id: agencyId,
        code: input.code.trim().toUpperCase().slice(0, 8),
        label: input.label?.trim() ?? "",
        coverage_value: input.coverageValue ?? 0,
        color: input.color ?? null,
        sort_order: input.sortOrder ?? count ?? 0,
      })
      .select("*").single();
    if (error) throw error;
    const row = fromSnake<ShiftCode>(data) as ShiftCode;
    await cloudAudit("shift_code", row.id, "create", null, JSON.stringify(row));
    return row;
  },

  updateShiftCode: async (id: string, input: Partial<ShiftCode>): Promise<ShiftCode> => {
    const patch: Record<string, unknown> = {};
    if (input.code !== undefined) patch.code = input.code.trim().toUpperCase().slice(0, 8);
    if (input.label !== undefined) patch.label = input.label;
    if (input.coverageValue !== undefined) patch.coverage_value = input.coverageValue;
    if (input.color !== undefined) patch.color = input.color;
    if (input.sortOrder !== undefined) patch.sort_order = input.sortOrder;
    const { data, error } = await supabase.from("shift_codes")
      .update(patch).eq("id", id).select("*").single();
    if (error) throw error;
    const row = fromSnake<ShiftCode>(data) as ShiftCode;
    await cloudAudit("shift_code", id, "update", null, JSON.stringify(row));
    return row;
  },

  deleteShiftCode: async (id: string): Promise<void> => {
    const { data: prior } = await supabase.from("shift_codes")
      .select("*").eq("id", id).maybeSingle();
    const { error } = await supabase.from("shift_codes").delete().eq("id", id);
    if (error) throw error;
    await cloudAudit("shift_code", id, "delete",
      prior ? JSON.stringify(prior) : null, null);
  },


  // ------------- Demo data (Supabase) -------------
  seedAgencyRoster: async (): Promise<number> => {
    const agencyId = await cloudAgencyId();
    const dept = await api.getDefaultDepartment();
    const roster: Array<[string, string]> = [
      ["Drain",       "Cpt"],
      ["Cain",        "Lt"],
      ["Mize",        "Msgt"],
      ["Hendrix",     "Msgt"],
      ["Taylor",      "Msgt"],
      ["Bond",        "Sgt"],
      ["Feith",       "Cpt"],
      ["Markstone",   "Msgt"],
      ["Grisham",     "Cpt"],
      ["Smith",       "Lt"],
      ["Bowen",       "Cpl"],
      ["Sotelo",      "Ofcr"],
      ["Bills",       "Ofcr"],
      ["Reyes",       "Ofcr"],
      ["Mauk",        "Ofcr"],
      ["Rockenfield", "Cpt"],
      ["Straughn",    "Lt"],
      ["Laub",        "Ofcr"],
      ["Maxwell",     "Ofcr"],
      ["Wagoner",     "Ofcr"],
      ["Jackowski",   "Ofc"],
    ];
    const oicRanks = new Set(["Cpt", "Lt", "Msgt", "Sgt", "Cpl"]);
    const rows = roster.map(([lastName, rank]) => ({
      agency_id: agencyId,
      department_id: dept.id,
      first_name: "",
      last_name: lastName,
      rank,
      status: "active",
      is_oic: oicRanks.has(rank),
      level: "l4",
      emails: [] as string[],
      phones: [] as string[],
    }));
    const { error } = await supabase.from("employees").insert(rows);
    if (error) throw error;
    return rows.length;
  },

  // ------------- Subscriptions (Supabase + Stripe) -------------
  //
  // The agency-scoped subscription row, written by the
  // stripe-webhook Edge Function only. Clients may READ via the
  // RLS policy "read agency subscription" so the UI can show a
  // trial / past-due / canceled banner.
  getMySubscription: async (): Promise<Subscription | null> => {
    const agencyId = await cloudAgencyId();
    const { data, error } = await supabase.from("subscriptions")
      .select("*").eq("agency_id", agencyId).maybeSingle();
    if (error) throw error;
    return data ? (fromSnake<Subscription>(data) as Subscription) : null;
  },

  // Open a Stripe Checkout session for a brand-new subscription.
  // Public — anyone on the marketing landing page can call this
  // without a Supabase session. Returns the Stripe-hosted checkout
  // URL; the caller redirects the browser to it.
  startSubscriptionCheckout: async (input: {
    email: string;
    agencyName?: string;
  }): Promise<{ url: string }> => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const res = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: anonKey ?? "",
      },
      body: JSON.stringify(input),
    });
    const text = await res.text();
    let parsed: { url?: string; error?: string } = {};
    try { parsed = JSON.parse(text); } catch { /* non-JSON */ }
    if (!res.ok || !parsed.url) {
      throw new Error(parsed.error ?? `Checkout failed (HTTP ${res.status})`);
    }
    return { url: parsed.url };
  },

  // Open a Stripe Customer Portal link for the signed-in Owner.
  // The portal handles card updates, invoice download, plan
  // changes, and cancellation — Stripe-hosted, no UI for us.
  startBillingPortal: async (): Promise<{ url: string }> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Not signed in");
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const res = await fetch(`${supabaseUrl}/functions/v1/create-billing-portal-session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: anonKey ?? "",
        Authorization: `Bearer ${session.access_token}`,
      },
    });
    const text = await res.text();
    let parsed: { url?: string; error?: string } = {};
    try { parsed = JSON.parse(text); } catch { /* non-JSON */ }
    if (!res.ok || !parsed.url) {
      throw new Error(parsed.error ?? `Billing portal failed (HTTP ${res.status})`);
    }
    return { url: parsed.url };
  },

  seedDemoEmployees: async (): Promise<number> => {
    const agencyId = await cloudAgencyId();
    const dept = await api.getDefaultDepartment();
    const shifts = await api.listShifts({ departmentId: dept.id });
    if (shifts.length === 0) throw new Error("Create at least one shift first");

    const { count } = await supabase.from("employees")
      .select("*", { count: "exact", head: true });
    const startBadge = 1001 + (count ?? 0);

    const roster: Array<[string, string, string]> = [
      ["Adams",    "James",   "Cpt"],
      ["Baxter",   "Linda",   "Lt"],
      ["Carter",   "Marcus",  "Lt"],
      ["Davis",    "Robin",   "Msgt"],
      ["Edwards",  "Sam",     "Msgt"],
      ["Fischer",  "Jamie",   "Msgt"],
      ["Gomez",    "Alex",    "Sgt"],
      ["Harris",   "Taylor",  "Sgt"],
      ["Iverson",  "Morgan",  "Sgt"],
      ["Johnson",  "Casey",   "Sgt"],
      ["Kim",      "Drew",    "Cpl"],
      ["Lopez",    "Avery",   "Cpl"],
      ["Mendez",   "Jordan",  "Ofcr"],
      ["Nguyen",   "Riley",   "Ofcr"],
      ["Ortiz",    "Quinn",   "Ofcr"],
      ["Patel",    "Reese",   "Ofcr"],
      ["Quinn",    "Parker",  "Ofcr"],
      ["Ramirez",  "Dakota",  "Ofcr"],
      ["Santos",   "Emery",   "Ofcr"],
      ["Turner",   "Finley",  "Ofcr"],
      ["Ueda",     "Skyler",  "Ofcr"],
      ["Vargas",   "River",   "Ofcr"],
      ["Wilson",   "Hayden",  "Ofcr"],
      ["Xiong",    "Sage",    "Ofcr"],
      ["Young",    "Rowan",   "Ofcr"],
    ];

    const oicRanks = new Set(["Cpt", "Lt", "Msgt", "Sgt", "Cpl"]);
    const rows = roster.map(([lastName, firstName, rank], i) => ({
      agency_id: agencyId,
      department_id: dept.id,
      first_name: firstName,
      last_name: lastName,
      badge_number: String(startBadge + i),
      rank,
      status: "active",
      is_oic: oicRanks.has(rank),
      level: "l4",
      emails: [] as string[],
      phones: [] as string[],
      home_shift_id: shifts[i % shifts.length].id,
    }));

    const { error } = await supabase.from("employees").insert(rows);
    if (error) throw error;
    return rows.length;
  },

  // ------------- Feature flags (Beta opt-ins) -------------
  // Per-agency beta opt-ins. The owner-only visibility gate lives
  // in `useFeatureFlag` (src/lib/featureFlags.ts) — this helper
  // just persists the boolean. Owner gates the call site.
  setFeatureFlag: async (key: string, enabled: boolean): Promise<Agency> => {
    const a = await api.getAgency();
    // Merge into the existing bag instead of replacing it so toggling
    // one flag doesn't wipe the others. Postgres `||` on jsonb does
    // the merge server-side; we read-modify-write here because we
    // also want to preserve the cleaned shape (drop the key entirely
    // when disabling, rather than keeping `{"oncall": false}`).
    const next = { ...(a.featureFlags ?? {}) };
    if (enabled) next[key] = true;
    else delete next[key];
    const { data, error } = await supabase.from("agencies")
      .update({ feature_flags: next })
      .eq("id", a.id).select("*").single();
    if (error) throw error;
    const updated = fromSnake<Agency>(data) as Agency;
    await cloudAudit("agency", a.id, "update",
      JSON.stringify({ featureFlags: a.featureFlags }),
      JSON.stringify({ featureFlags: updated.featureFlags }));
    return updated;
  },

  // ------------- On-call rosters + assignments -------------
  // Specialty rotations (Detectives, SWAT, K-9, Animal Control, …)
  // that don't affect shift coverage. Lives in its own pair of
  // tables (`on_call_rosters`, `on_call_assignments`) so the
  // schedule grid stays unaware. Owner-only beta behind the
  // "oncall" feature flag during rollout.
  listOnCallRosters: async (includeArchived = false): Promise<OnCallRoster[]> => {
    let q = supabase.from("on_call_rosters").select("*")
      .order("sort_order", { ascending: true })
      .order("name", { ascending: true });
    if (!includeArchived) q = q.is("archived_at", null);
    const { data, error } = await q;
    if (error) throw error;
    return fromSnakeList<OnCallRoster>(data);
  },

  createOnCallRoster: async (input: {
    name: string;
    color?: string;
    hasSecondary?: boolean;
    radioChannel?: string | null;
    contactPhone?: string | null;
    sortOrder?: number;
  }): Promise<OnCallRoster> => {
    const agencyId = await cloudAgencyId();
    const { data, error } = await supabase.from("on_call_rosters").insert({
      agency_id: agencyId,
      name: input.name.trim(),
      color: input.color ?? "#64748b",
      has_secondary: input.hasSecondary ?? false,
      radio_channel: input.radioChannel ?? null,
      contact_phone: input.contactPhone ?? null,
      sort_order: input.sortOrder ?? 0,
    }).select("*").single();
    if (error) throw error;
    const saved = fromSnake<OnCallRoster>(data) as OnCallRoster;
    await cloudAudit("on_call_roster", saved.id, "create",
      null, JSON.stringify(saved));
    return saved;
  },

  updateOnCallRoster: async (id: string, patch: Partial<{
    name: string;
    color: string;
    hasSecondary: boolean;
    radioChannel: string | null;
    contactPhone: string | null;
    sortOrder: number;
  }>): Promise<OnCallRoster> => {
    const { data: prior } = await supabase.from("on_call_rosters")
      .select("*").eq("id", id).maybeSingle();
    const update: Record<string, unknown> = {};
    if (patch.name !== undefined) update.name = patch.name.trim();
    if (patch.color !== undefined) update.color = patch.color;
    if (patch.hasSecondary !== undefined) update.has_secondary = patch.hasSecondary;
    if (patch.radioChannel !== undefined) update.radio_channel = patch.radioChannel;
    if (patch.contactPhone !== undefined) update.contact_phone = patch.contactPhone;
    if (patch.sortOrder !== undefined) update.sort_order = patch.sortOrder;
    const { data, error } = await supabase.from("on_call_rosters")
      .update(update).eq("id", id).select("*").single();
    if (error) throw error;
    const saved = fromSnake<OnCallRoster>(data) as OnCallRoster;
    await cloudAudit("on_call_roster", id, "update",
      prior ? JSON.stringify(prior) : null, JSON.stringify(saved));
    return saved;
  },

  // Soft-delete via archived_at so historical assignments aren't
  // orphaned. Set null to un-archive.
  archiveOnCallRoster: async (id: string, archived: boolean): Promise<void> => {
    const { error } = await supabase.from("on_call_rosters")
      .update({ archived_at: archived ? new Date().toISOString() : null })
      .eq("id", id);
    if (error) throw error;
    await cloudAudit("on_call_roster", id, "update",
      null, JSON.stringify({ archived }));
  },

  // Date-range overlap query. Returns every assignment for the
  // given roster that overlaps [startDate, endDate] inclusive.
  // Pass rosterId = null to fan across every roster (used by the
  // Schedule "today's on-call" strip).
  listOnCallAssignments: async (
    rosterId: string | null,
    startDate: string,
    endDate: string,
  ): Promise<OnCallAssignment[]> => {
    let q = supabase.from("on_call_assignments").select("*")
      .lte("start_date", endDate)
      .gte("end_date", startDate)
      .order("start_date", { ascending: true });
    if (rosterId) q = q.eq("roster_id", rosterId);
    const { data, error } = await q;
    if (error) throw error;
    return fromSnakeList<OnCallAssignment>(data);
  },

  createOnCallAssignment: async (input: {
    rosterId: string;
    employeeId: string;
    startDate: string;
    endDate: string;
    role?: "primary" | "secondary";
    notes?: string | null;
  }): Promise<OnCallAssignment> => {
    const agencyId = await cloudAgencyId();
    const { data: { session } } = await supabase.auth.getSession();
    const { data, error } = await supabase.from("on_call_assignments").insert({
      agency_id: agencyId,
      roster_id: input.rosterId,
      employee_id: input.employeeId,
      start_date: input.startDate,
      end_date: input.endDate,
      role: input.role ?? "primary",
      notes: input.notes ?? null,
      created_by: session?.user?.id ?? null,
    }).select("*").single();
    if (error) throw error;
    const saved = fromSnake<OnCallAssignment>(data) as OnCallAssignment;
    await cloudAudit("on_call_assignment", saved.id, "create",
      null, JSON.stringify(saved));
    return saved;
  },

  updateOnCallAssignment: async (id: string, patch: Partial<{
    employeeId: string;
    startDate: string;
    endDate: string;
    role: "primary" | "secondary";
    notes: string | null;
  }>): Promise<OnCallAssignment> => {
    const { data: prior } = await supabase.from("on_call_assignments")
      .select("*").eq("id", id).maybeSingle();
    const update: Record<string, unknown> = {};
    if (patch.employeeId !== undefined) update.employee_id = patch.employeeId;
    if (patch.startDate !== undefined) update.start_date = patch.startDate;
    if (patch.endDate !== undefined) update.end_date = patch.endDate;
    if (patch.role !== undefined) update.role = patch.role;
    if (patch.notes !== undefined) update.notes = patch.notes;
    const { data, error } = await supabase.from("on_call_assignments")
      .update(update).eq("id", id).select("*").single();
    if (error) throw error;
    const saved = fromSnake<OnCallAssignment>(data) as OnCallAssignment;
    await cloudAudit("on_call_assignment", id, "update",
      prior ? JSON.stringify(prior) : null, JSON.stringify(saved));
    return saved;
  },

  deleteOnCallAssignment: async (id: string): Promise<void> => {
    const { data: prior } = await supabase.from("on_call_assignments")
      .select("*").eq("id", id).maybeSingle();
    const { error } = await supabase.from("on_call_assignments").delete().eq("id", id);
    if (error) throw error;
    await cloudAudit("on_call_assignment", id, "delete",
      prior ? JSON.stringify(prior) : null, null);
  },
};
